import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useBackButton } from "../hooks/useBackButton";
import { API, useAuth, useSharedData, getAuthenticatedMediaUrl } from "@/App";
import { toast } from "sonner";
import PhoneLookupPopup from "@/components/PhoneLookupPopup";
import ImageLightbox from "@/components/ImageLightbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import {
  Search,
  Filter,
  Plus,
  FileText,
  MapPin,
  Calendar as CalendarIcon,
  Clock,
  ChevronRight,
  ChevronLeft,
  List,
  LayoutGrid,
  MapPinned,
  UserCircle,
  Users,
  X,
  Phone,
  AlertTriangle,
  ClipboardPaste,
  RefreshCw,
  PhoneIncoming,
  Flag,
  Pencil as Edit,
  Shield,
  ChevronUp,
  ChevronDown
} from "lucide-react";
import { format } from "date-fns";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import markerRetina from "leaflet/dist/images/marker-icon-2x.png";

const incidentMapMarkerIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerRetina,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

function IncidentsMapFitBounds({ markers }) {
  const map = useMap();
  useEffect(() => {
    if (!markers.length) return;
    const bounds = L.latLngBounds(markers.map(m => [m.lat, m.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
  }, [markers, map]);
  return null;
}

const STATUS_OPTIONS = ["live_call", "log", "report", "investigating", "arrest", "closed"];
const PRIORITY_OPTIONS = ["low", "medium", "high", "critical"];

// Format time in New York timezone without timezone label
const formatNYTime = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleString('en-US', { 
      timeZone: 'America/New_York',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (e) { return String(dateStr); }
};

export default function Incidents() {
  const { user } = useAuth();
  const sharedData = useSharedData();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasAccess = user?.role === "admin" || user?.incidents_access;
  const [incidents, setIncidents] = useState([]);
  const [incidentTypes, setIncidentTypes] = useState([]);
  const [units, setUnits] = useState([]);
  const [dispatchers, setDispatchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewDialog, setShowNewDialog] = useState(false);
  // Short form — the same newIncident state, only the fields a dispatcher
  // actually fills in when logging an incident after the fact.
  const [showQuickDialog, setShowQuickDialog] = useState(false);
  const [quickUnitSearch, setQuickUnitSearch] = useState("");
  const [quickUnitHighlight, setQuickUnitHighlight] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // Sort state
  const [sortOrder, setSortOrder] = useState(searchParams.get("sort") || "newest");
  const [sortField, setSortField] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(50);

  // View mode
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("incidents_view") || "cards");
  // Map view state — fetched lazily when the user switches to "map" mode.
  const [incidentsMapData, setIncidentsMapData] = useState([]);
  const [incidentsMapLoading, setIncidentsMapLoading] = useState(false);
  const [incidentsMapLoaded, setIncidentsMapLoaded] = useState(false);
  const fetchIncidentsMapData = useCallback(async () => {
    setIncidentsMapLoading(true);
    try {
      const res = await axios.get(`${API}/incidents/map-data`, { withCredentials: true });
      setIncidentsMapData(res.data?.locations || []);
      setIncidentsMapLoaded(true);
    } catch (e) {
      toast.error("Failed to load incidents map");
    } finally {
      setIncidentsMapLoading(false);
    }
  }, []);
  const [mobileViewMode, setMobileViewMode] = useState(() => localStorage.getItem("incidents_mobile_view") || "cards");
  useEffect(() => {
    if ((viewMode === "map" || mobileViewMode === "map") && !incidentsMapLoaded) {
      fetchIncidentsMapData();
    }
  }, [viewMode, mobileViewMode, incidentsMapLoaded, fetchIncidentsMapData]);

  // Search category
  const [searchField, setSearchField] = useState("all"); // "all", "headline", "id", "address", "callback", "victim", "witness", "description", "notes"

  // Filters - initialize from URL params
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [filterType, setFilterType] = useState(searchParams.get("type") || "");
  const [filterStatus, setFilterStatus] = useState(searchParams.get("status") || "");
  const [filterPriority, setFilterPriority] = useState(searchParams.get("priority") ? searchParams.get("priority").split(",") : []);
  const [filterUnits, setFilterUnits] = useState(searchParams.get("units") ? searchParams.get("units").split(",") : []);
  const [filterDispatcher, setFilterDispatcher] = useState(searchParams.get("dispatcher") || "");
  const [filterArrest, setFilterArrest] = useState(searchParams.get("arrest") === "true");
  const [filterArrestVia, setFilterArrestVia] = useState(searchParams.get("arrestVia") === "true");
  const [filterPR, setFilterPR] = useState(searchParams.get("pr") === "true");
  const [dateRange, setDateRange] = useState({
    from: searchParams.get("from") ? new Date(searchParams.get("from")) : null,
    to: searchParams.get("to") ? new Date(searchParams.get("to")) : null
  });
  // Additional filters
  const [filterHateCrime, setFilterHateCrime] = useState(searchParams.get("hate") === "true");
  const [filterWeapon, setFilterWeapon] = useState(searchParams.get("weapon") === "true");
  const [filterPDActivated, setFilterPDActivated] = useState(searchParams.get("pd") === "true");
  const [filterHasVictims, setFilterHasVictims] = useState(searchParams.get("victims") === "true");
  const [filterHasWitnesses, setFilterHasWitnesses] = useState(searchParams.get("witnesses") === "true");
  const [filterHasPerps, setFilterHasPerps] = useState(searchParams.get("perps") === "true");
  const [filterHasMedia, setFilterHasMedia] = useState(searchParams.get("media") === "true");
  const [filterNeedsFollowup, setFilterNeedsFollowup] = useState(searchParams.get("followup") === "true");
  const [filterTransmitted1085, setFilterTransmitted1085] = useState(searchParams.get("t1085") === "true");
  const [filterTransmitted1013, setFilterTransmitted1013] = useState(searchParams.get("t1013") === "true");
  const [filterHotline, setFilterHotline] = useState(searchParams.get("hotline") === "true");
  const [filterReportType, setFilterReportType] = useState(searchParams.get("reportType") || "");
  // New filters
  const [filterConfirmation, setFilterConfirmation] = useState(searchParams.get("confirm") || "");
  const [filterHasAnyProfile, setFilterHasAnyProfile] = useState(searchParams.get("anyProfile") === "true");
  const [filterSpecificProfile, setFilterSpecificProfile] = useState(searchParams.get("profile") || "");
  const [filterReadyForArrest, setFilterReadyForArrest] = useState(searchParams.get("ready") === "true");
  const [filterProfileArrested, setFilterProfileArrested] = useState(searchParams.get("profArrested") === "true");
  const [filterUnitInvestigating, setFilterUnitInvestigating] = useState(searchParams.get("unitInv") || "");
  const [filterMinorOrElderly, setFilterMinorOrElderly] = useState(searchParams.get("minorElderly") === "true");
  const [filterInjuries, setFilterInjuries] = useState(searchParams.get("injuries") === "true");
  const [filterPoliceFollowup, setFilterPoliceFollowup] = useState(searchParams.get("policeFollowup") === "true");
  const [filterPoliceReport, setFilterPoliceReport] = useState(searchParams.get("policeReport") === "true");
  const [filterFootageObtained, setFilterFootageObtained] = useState(searchParams.get("footage") === "true");
  const [filterFootageRequested, setFilterFootageRequested] = useState(searchParams.get("footageReq") === "true");
  const [filterFromLiveCall, setFilterFromLiveCall] = useState(searchParams.get("liveCall") === "true");
  const [filterHasCallbacks, setFilterHasCallbacks] = useState(searchParams.get("callbacks") === "true");
  const [filterCallbackType, setFilterCallbackType] = useState(searchParams.get("cbType") || ""); // "cv", "3rd_party", or ""
  const [profiles, setProfiles] = useState([]);

  // Update URL when filters change
  const updateUrlParams = useCallback(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set("q", searchQuery);
    if (filterType) params.set("type", filterType);
    if (filterStatus) params.set("status", filterStatus);
    if (filterPriority.length) params.set("priority", filterPriority.join(","));
    if (filterUnits.length) params.set("units", filterUnits.join(","));
    if (filterDispatcher) params.set("dispatcher", filterDispatcher);
    if (filterArrest) params.set("arrest", "true");
    if (filterArrestVia) params.set("arrestVia", "true");
    if (filterPR) params.set("pr", "true");
    if (dateRange.from) params.set("from", dateRange.from.toISOString().split("T")[0]);
    if (dateRange.to) params.set("to", dateRange.to.toISOString().split("T")[0]);
    if (filterHateCrime) params.set("hate", "true");
    if (filterWeapon) params.set("weapon", "true");
    if (filterPDActivated) params.set("pd", "true");
    if (filterHasVictims) params.set("victims", "true");
    if (filterHasWitnesses) params.set("witnesses", "true");
    if (filterHasPerps) params.set("perps", "true");
    if (filterHasMedia) params.set("media", "true");
    if (filterNeedsFollowup) params.set("followup", "true");
    if (filterTransmitted1085) params.set("t1085", "true");
    if (filterTransmitted1013) params.set("t1013", "true");
    if (filterHotline) params.set("hotline", "true");
    if (filterReportType) params.set("reportType", filterReportType);
    if (filterConfirmation) params.set("confirm", filterConfirmation);
    if (filterHasAnyProfile) params.set("anyProfile", "true");
    if (filterSpecificProfile) params.set("profile", filterSpecificProfile);
    if (filterReadyForArrest) params.set("ready", "true");
    if (filterProfileArrested) params.set("profArrested", "true");
    if (filterUnitInvestigating) params.set("unitInv", filterUnitInvestigating);
    if (filterMinorOrElderly) params.set("minorElderly", "true");
    if (filterInjuries) params.set("injuries", "true");
    if (filterPoliceFollowup) params.set("policeFollowup", "true");
    if (filterPoliceReport) params.set("policeReport", "true");
    if (filterFootageObtained) params.set("footage", "true");
    if (filterFootageRequested) params.set("footageReq", "true");
    if (filterFromLiveCall) params.set("liveCall", "true");
    if (filterHasCallbacks) params.set("callbacks", "true");
    if (filterCallbackType) params.set("cbType", filterCallbackType);
    if (sortOrder !== "newest") params.set("sort", sortOrder);
    setSearchParams(params, { replace: true });
  }, [searchQuery, filterType, filterStatus, filterPriority, filterUnits, filterDispatcher, filterArrest, filterArrestVia, filterPR, dateRange, filterHateCrime, filterWeapon, filterPDActivated, filterHasVictims, filterHasWitnesses, filterHasPerps, filterHasMedia, filterNeedsFollowup, filterTransmitted1085, filterTransmitted1013, filterHotline, filterReportType, filterConfirmation, filterHasAnyProfile, filterSpecificProfile, filterReadyForArrest, filterProfileArrested, filterUnitInvestigating, filterMinorOrElderly, filterInjuries, filterPoliceFollowup, filterPoliceReport, filterFootageObtained, filterFootageRequested, filterFromLiveCall, filterHasCallbacks, filterCallbackType, sortOrder, setSearchParams]);

  // Sync filters to URL whenever they change
  useEffect(() => {
    updateUrlParams();
  }, [updateUrlParams]);

  // New incident form - expanded like LiveCalls
  const [newIncident, setNewIncident] = useState({
    headline: "",
    date_time: new Date().toISOString(),
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    address: "",
    location_title: "",
    cross_streets: "",
    location_notes: "",
    incident_type: "",
    secondary_incident_type: "",
    report_type: "reported_live",
    status: "report",
    priority: "medium",
    callback_numbers: [],
    cv_or_third_party: "",
    caller_has_visual: false,
    victims: [],
    witnesses: [],
    perp_descriptions: [],
    direction_of_flight: "",
    confirmation_status: "",
    transmitted_10_85: false,
    transmitted_10_13: false,
    pd_activated: false,
    ems_required: false,
    is_hotline_call: false,
    is_hate_crime: false,
    dispatch_id: "",
    units_responded: [],
    footage_requested: false,
    job_body: "",
    notes: [],
    additional_locations: [],
    send_whatsapp_to_taru: true
  });

  // Additional location dialog
  const [showAddLocDialog, setShowAddLocDialog] = useState(false);
  const [editingLocIndex, setEditingLocIndex] = useState(null);
  const [addLocData, setAddLocData] = useState({ title: "", location: "", location_notes: "", cross_streets: "", direction_of_flight: "" });
  const [addLocPredictions, setAddLocPredictions] = useState([]);
  const [showAddLocPredictions, setShowAddLocPredictions] = useState(false);
  const [addLocCrossLoading, setAddLocCrossLoading] = useState(false);
  const addLocInputRef = useRef(null);
  const [deleteLocIndex, setDeleteLocIndex] = useState(null);
  const [deleteCallbackIndex, setDeleteCallbackIndex] = useState(null);

  const [showPhoneNumbers, setShowPhoneNumbers] = useState(false);
  const [showCreatedDate, setShowCreatedDate] = useState(false);

  // Call type autocomplete state
  const [callTypeSearch, setCallTypeSearch] = useState("");
  const [showCallTypeDropdown, setShowCallTypeDropdown] = useState(false);
  const [callTypeHighlightedIndex, setCallTypeHighlightedIndex] = useState(-1);
  const [secondaryCallTypeSearch, setSecondaryCallTypeSearch] = useState("");
  const [showSecondaryCallTypeDropdown, setShowSecondaryCallTypeDropdown] = useState(false);
  const [secondaryCallTypeHighlightedIndex, setSecondaryCallTypeHighlightedIndex] = useState(-1);

  // Callback number input
  const [newCallbackNumber, setNewCallbackNumber] = useState({ number: "", caller_type: "", has_visual: false });

  // Recent incoming calls (30 min)
  const [showRecentCalls, setShowRecentCalls] = useState(false);
  const [recentIncomingCalls, setRecentIncomingCalls] = useState([]);
  const [loadingRecentCalls, setLoadingRecentCalls] = useState(false);

  // Phone lookup popup
  const [phoneLookupNumber, setPhoneLookupNumber] = useState(null);

  // Victim/Witness inputs
  const [newVictim, setNewVictim] = useState({ name: "", contact: "", showInput: false });
  const [newWitness, setNewWitness] = useState({ name: "", contact: "", showInput: false });

  // Perp input - full form like LiveCalls
  const [showPerpDialog, setShowPerpDialog] = useState(false);
  const perpDialogJustClosedRef = useRef(false);
  const [showLiveCallOptions, setShowLiveCallOptions] = useState(false);
  const [showExtraDetails, setShowExtraDetails] = useState(false);
  const [editingPerpIndex, setEditingPerpIndex] = useState(null);
  const [profileSearchQuery, setProfileSearchQuery] = useState("");
  const [profileMugshotLightbox, setProfileMugshotLightbox] = useState(null);
  const [newPerp, setNewPerp] = useState({
    name: "", address: "", current_location: "", sex: "", race: "", age: "", height: "", weight: "",
    build: "", complexion: "", hair_color_cut: "", eye_color: "", hat_color_style: "",
    shirt: "", pants: "", jacket_coat: "", shoes: "", visible_marks: "", clothing: "",
    direction_of_flight: "", has_weapon: false, weapon_description: "",
    vehicle_description: "", license_plate: "", was_arrested: false, notes: "",
    linked_profile_id: null, linked_profile_name: null
  });
  const resetNewPerp = () => setNewPerp({
    name: "", address: "", current_location: "", sex: "", race: "", age: "", height: "", weight: "",
    build: "", complexion: "", hair_color_cut: "", eye_color: "", hat_color_style: "",
    shirt: "", pants: "", jacket_coat: "", shoes: "", visible_marks: "", clothing: "",
    direction_of_flight: "", has_weapon: false, weapon_description: "",
    vehicle_description: "", license_plate: "", was_arrested: false, notes: "",
    linked_profile_id: null, linked_profile_name: null
  });

  // Cross streets loading
  const [crossStreetsLoading, setCrossStreetsLoading] = useState(false);
  const [enableGoogleLookup, setEnableGoogleLookup] = useState(false);

  // Location autocomplete state
  const [locationPredictions, setLocationPredictions] = useState([]);
  const [showLocationPredictions, setShowLocationPredictions] = useState(false);
  const [locationAutocompleteLoading, setLocationAutocompleteLoading] = useState(false);
  const [enableLocationPredictions, setEnableLocationPredictions] = useState(false);
  const locationAutocompleteTimeoutRef = useRef(null);
  const crossStreetsTimeoutRef = useRef(null);
  const addressInputRef = useRef(null);
  const scrollAreaRef = useRef(null);

  // Restore scroll position when returning from incident detail
  useEffect(() => {
    const savedScroll = sessionStorage.getItem("incidents_scroll");
    if (savedScroll && scrollAreaRef.current) {
      const viewport = scrollAreaRef.current.querySelector("[data-radix-scroll-area-viewport]");
      if (viewport) {
        setTimeout(() => { viewport.scrollTop = parseInt(savedScroll, 10); }, 100);
        sessionStorage.removeItem("incidents_scroll");
      }
    }
  });

  const navigateToIncident = (incidentId) => {
    // Save scroll position of the <main> element (the actual scroll container)
    const mainEl = document.querySelector('main');
    if (mainEl) {
      sessionStorage.setItem("incidents_main_scroll", String(mainEl.scrollTop));
    }
    // Also save desktop ScrollArea scroll
    if (scrollAreaRef.current) {
      const viewport = scrollAreaRef.current.querySelector("[data-radix-scroll-area-viewport]");
      if (viewport) {
        sessionStorage.setItem("incidents_scroll", String(viewport.scrollTop));
      }
    }
    sessionStorage.setItem("incidents_current_page", String(currentPage));
    sessionStorage.setItem("incidents_nav_list", JSON.stringify(sortedIncidents.map(i => i.incident_id)));
    // Preserve the current filtered URL so the detail-page Back button can
    // restore the exact same filter/search state instead of resetting to /incidents.
    sessionStorage.setItem("incidents_return_url", `/incidents${window.location.search}`);
    navigate(`/incidents/${incidentId}`);
  };

  // Restore page and scroll position after data loads
  useEffect(() => {
    if (loading) return;
    const savedPage = sessionStorage.getItem("incidents_current_page");
    const savedScroll = sessionStorage.getItem("incidents_main_scroll");
    if (!savedPage && !savedScroll) return;

    if (savedPage) {
      skipPageResetRef.current = true;
      setCurrentPage(parseInt(savedPage, 10));
      sessionStorage.removeItem("incidents_current_page");
    }
    if (savedScroll) {
      const scrollTop = parseInt(savedScroll, 10);
      sessionStorage.removeItem("incidents_main_scroll");
      // Restore after React renders with the correct page
      setTimeout(() => {
        const mainEl = document.querySelector('main');
        if (mainEl) mainEl.scrollTop = scrollTop;
      }, 150);
    }
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hasAccess) fetchData();
  }, [hasAccess]);

  // Handle callback query param from Hotline page
  useEffect(() => {
    if (searchParams.get("new") === "true") {
      const callbackParam = searchParams.get("callback");
      if (callbackParam) {
        setNewIncident(prev => ({
          ...prev,
          callback_numbers: [{ number: callbackParam, caller_type: "", has_visual: false }]
        }));
      }
      setShowNewDialog(true);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("new");
      newParams.delete("callback");
      setSearchParams(newParams, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Pickup from hotline alert — pre-fill callback numbers and open new incident dialog
  const pickupHotlineAlert = useCallback(() => {
    const stored = sessionStorage.getItem("hotline_callback_numbers");
    if (!stored) return;
    try {
      const numbers = JSON.parse(stored);
      if (Array.isArray(numbers) && numbers.length > 0) {
        setNewIncident(prev => ({
          ...prev,
          callback_numbers: numbers,
          is_hotline_call: true,
        }));
        setShowNewDialog(true);
      }
      sessionStorage.removeItem("hotline_callback_numbers");
    } catch (e) {
      console.error("Failed to parse hotline callback numbers:", e);
    }
    // Clean URL if param is present
    if (new URLSearchParams(window.location.search).get("from_hotline_alert")) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    // Delay slightly to ensure component is fully mounted before opening dialog
    const timer = setTimeout(() => {
      if (new URLSearchParams(window.location.search).get("from_hotline_alert") === "1") {
        pickupHotlineAlert();
      }
    }, 100);
    // Also listen for custom event (fires when already on this page)
    const handler = (e) => {
      if (e.detail?.target && e.detail.target !== "/incidents") return;
      setTimeout(pickupHotlineAlert, 100);
    };
    window.addEventListener("hotline-alert-pickup", handler);
    return () => { clearTimeout(timer); window.removeEventListener("hotline-alert-pickup", handler); };
  }, [pickupHotlineAlert]);

  // Handle mobile back button for dialogs
  useBackButton(showNewDialog, () => { if (!showPerpDialog && !showExtraDetails) setShowNewDialog(false); });
  useBackButton(showQuickDialog, () => { if (!showPerpDialog) setShowQuickDialog(false); });
  useBackButton(showFilters, () => setShowFilters(false));

  const fetchData = async () => {
    try {
      const [incidentsRes, typesData, unitsData, dispatchersRes, profilesData] = await Promise.all([
        axios.get(`${API}/incidents`, { params: { limit: 500 }, withCredentials: true }),
        sharedData?.getIncidentTypes().catch(() => []),
        sharedData?.getUnits().catch(() => []),
        axios.get(`${API}/units/dispatchers`, { withCredentials: true }),
        sharedData?.getProfiles().catch(() => [])
      ]);
      setIncidents(incidentsRes.data);
      setIncidentTypes(Array.isArray(typesData) ? typesData : []);
      setUnits(Array.isArray(unitsData) ? unitsData : []);
      setDispatchers(dispatchersRes.data);
      setProfiles(profilesData?.profiles || profilesData || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load incidents");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      if (filterType) params.append("incident_type", filterType);
      if (filterStatus) params.append("status", filterStatus);
      if (filterPriority.length > 0) params.append("priority", filterPriority.join(","));
      if (filterUnits.length > 0) params.append("units", filterUnits.join(","));
      if (filterDispatcher) params.append("dispatcher", filterDispatcher);
      if (dateRange.from) params.append("start_date", dateRange.from.toISOString());
      if (dateRange.to) params.append("end_date", dateRange.to.toISOString());

      const response = await axios.get(`${API}/incidents?${params.toString()}`, { withCredentials: true });
      setIncidents(response.data);
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  // Export handlers moved to Settings → Data tab. The Incidents page no
  // longer offers an Export button; admins use Settings → Data → Exports.

  // Format phone number - handles US and international formats
  const formatPhoneNumber = (value) => {
    if (!value) return "";
    const hasPlus = value.startsWith('+');
    let digits = value.replace(/\D/g, '');
    if (hasPlus) {
      if (digits.startsWith('1') && digits.length <= 11) {
        const countryCode = digits.slice(0, 1);
        const rest = digits.slice(1);
        if (rest.length === 0) return `+${countryCode}`;
        if (rest.length <= 3) return `+${countryCode}-${rest}`;
        if (rest.length <= 6) return `+${countryCode}-${rest.slice(0, 3)}-${rest.slice(3)}`;
        return `+${countryCode}-${rest.slice(0, 3)}-${rest.slice(3, 6)}-${rest.slice(6, 10)}`;
      }
      if (digits.length <= 3) return `+${digits}`;
      let countryCodeLen = 1;
      if (!digits.startsWith('1')) countryCodeLen = digits.length >= 12 ? 3 : 2;
      const countryCode = digits.slice(0, countryCodeLen);
      const localNumber = digits.slice(countryCodeLen);
      if (localNumber.length === 0) return `+${countryCode}`;
      return `+${countryCode}-${localNumber}`;
    }
    if (digits.startsWith('1') && digits.length === 11) {
      return `+1-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7, 11)}`;
    }
    if (digits.length > 10) {
      if (digits.startsWith('1')) return `+1-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7, 11)}`;
      return `+${digits.slice(0, 2)}-${digits.slice(2)}`;
    }
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  // Recent incoming calls
  const fetchRecentIncomingCalls = async () => {
    setLoadingRecentCalls(true);
    try {
      const response = await axios.get(`${API}/incoming-calls/recent`, { withCredentials: true });
      setRecentIncomingCalls(response.data);
    } catch (error) {
      console.error("Failed to fetch recent calls:", error);
    } finally {
      setLoadingRecentCalls(false);
    }
  };

  const formatCallTime = (isoString) => {
    if (!isoString) return "";
    try {
      const d = new Date(isoString);
      return d.toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit", hour12: true });
    } catch { return ""; }
  };

  // Check if address starts with a house number (e.g. "580 Crown St" not just "Crown St")
  const hasHouseNumber = (addr) => /^\d+\s/.test(addr?.trim() || "");

  // Fetch cross streets from Google Maps
  const fetchCrossStreets = async (address, setterFn) => {
    if (!address || address.length < 5) return;
    setCrossStreetsLoading(true);
    try {
      const response = await axios.post(
        `${API}/geocoding/cross-streets`,
        { address },
        { withCredentials: true }
      );
      if (response.data?.cross_streets) {
        if (setterFn) {
          setterFn(response.data.cross_streets);
        } else {
          setNewIncident(prev => ({ ...prev, cross_streets: response.data.cross_streets }));
        }
      }
    } catch (error) {
      console.error("Cross streets lookup failed:", error);
    } finally {
      setCrossStreetsLoading(false);
    }
  };

  // Fetch location autocomplete predictions
  const fetchLocationPredictions = async (input, setterFn, setShowFn) => {
    if (!input || input.length < 3) {
      (setterFn || setLocationPredictions)([]);
      (setShowFn || setShowLocationPredictions)(false);
      return;
    }

    setLocationAutocompleteLoading(true);
    try {
      const response = await axios.post(
        `${API}/geocoding/autocomplete`,
        { input },
        { withCredentials: true }
      );

      if (response.data.predictions?.length > 0) {
        (setterFn || setLocationPredictions)(response.data.predictions);
        (setShowFn || setShowLocationPredictions)(true);
      } else {
        (setterFn || setLocationPredictions)([]);
        (setShowFn || setShowLocationPredictions)(false);
      }
    } catch (error) {
      console.error("Location autocomplete failed:", error);
      (setterFn || setLocationPredictions)([]);
      (setShowFn || setShowLocationPredictions)(false);
    } finally {
      setLocationAutocompleteLoading(false);
    }
  };

  // Select a location prediction
  const selectLocationPrediction = (prediction) => {
    const locationValue = prediction.main_text || prediction.description;
    setNewIncident(prev => ({ ...prev, address: locationValue }));
    setLocationPredictions([]);
    setShowLocationPredictions(false);

    // Trigger cross streets lookup if enabled and address has a house number
    if (enableGoogleLookup && locationValue.length >= 5 && hasHouseNumber(locationValue)) {
      fetchCrossStreets(locationValue);
    }
  };

  // Handle address change with debounced autocomplete
  const handleAddressChange = (value) => {
    setNewIncident(prev => ({ ...prev, address: value }));

    // Clear any pending timeouts
    if (locationAutocompleteTimeoutRef.current) {
      clearTimeout(locationAutocompleteTimeoutRef.current);
    }

    // Fetch location predictions with debounce - only if enabled
    if (enableLocationPredictions && value.length >= 3) {
      locationAutocompleteTimeoutRef.current = setTimeout(() => {
        fetchLocationPredictions(value);
      }, 300);
    } else {
      setLocationPredictions([]);
      setShowLocationPredictions(false);
    }

    // Cross streets: once the address is fully entered, on leaving the field
    // (handleAddressBlur) — this fired a ~56-request lookup on every keystroke.
  };
  const handleAddressBlur = () => {
    const a = (newIncident.address || "").trim();
    if (enableGoogleLookup && hasHouseNumber(a) && /^\d+[-\w]*\s+.*[a-z]{3,}/i.test(a)) {
      fetchCrossStreets(a);
    }
  };

  // Additional location dialog handlers
  const openAddLocDialog = (index = null) => {
    if (index !== null) {
      const loc = newIncident.additional_locations[index];
      setAddLocData({ ...loc });
      setEditingLocIndex(index);
    } else {
      setAddLocData({ title: "", location: "", location_notes: "", cross_streets: "", direction_of_flight: "" });
      setEditingLocIndex(null);
    }
    setAddLocPredictions([]);
    setShowAddLocPredictions(false);
    setShowAddLocDialog(true);
  };

  const saveAddLoc = () => {
    // Original location: the address is edited inline on the form, so the
    // dialog only collects the optional extras. Don't require a location here
    // (the dialog has no location field for this case) — just carry the
    // current address through with the updated details.
    if (editingLocIndex === "original") {
      setNewIncident(prev => ({
        ...prev,
        address: addLocData.location,
        location_title: addLocData.title || "",
        location_notes: addLocData.location_notes || "",
        cross_streets: addLocData.cross_streets || "",
        direction_of_flight: addLocData.direction_of_flight || "",
      }));
      setShowAddLocDialog(false);
      return;
    }

    if (!addLocData.location.trim()) { toast.error("Enter a location"); return; }

    const locs = [...(newIncident.additional_locations || [])];
    if (editingLocIndex !== null) {
      locs[editingLocIndex] = { ...addLocData };
    } else {
      locs.push({ ...addLocData });
    }
    setNewIncident(prev => ({ ...prev, additional_locations: locs }));
    setShowAddLocDialog(false);
  };

  const confirmDeleteAddLoc = () => {
    if (deleteLocIndex === null) return;
    const locs = (newIncident.additional_locations || []).filter((_, i) => i !== deleteLocIndex);
    setNewIncident(prev => ({ ...prev, additional_locations: locs }));
    setDeleteLocIndex(null);
  };

  const handleAddLocLocationChange = (value) => {
    setAddLocData(prev => ({ ...prev, location: value }));
    if (enableLocationPredictions && value.length >= 3) {
      if (locationAutocompleteTimeoutRef.current) clearTimeout(locationAutocompleteTimeoutRef.current);
      locationAutocompleteTimeoutRef.current = setTimeout(() => {
        fetchLocationPredictions(value, setAddLocPredictions, setShowAddLocPredictions);
      }, 300);
    } else {
      setAddLocPredictions([]);
      setShowAddLocPredictions(false);
    }
    // Cross streets: on leaving the field (onBlur below), not while typing.
  };

  const selectAddLocPrediction = (prediction) => {
    const val = prediction.main_text || prediction.description;
    setAddLocData(prev => ({ ...prev, location: val }));
    setAddLocPredictions([]);
    setShowAddLocPredictions(false);
    if (enableGoogleLookup && val.length >= 5 && hasHouseNumber(val)) {
      fetchCrossStreets(val, (cs) => setAddLocData(prev => ({ ...prev, cross_streets: cs })));
    }
  };

  const resetNewIncidentForm = () => {
    setNewIncident({
      headline: "",
      date_time: new Date().toISOString(),
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().slice(0, 5),
      address: "",
      location_title: "",
      cross_streets: "",
      location_notes: "",
      incident_type: "",
      secondary_incident_type: "",
      report_type: "reported_live",
      status: "report",
      priority: "medium",
      callback_numbers: [],
      cv_or_third_party: "",
      caller_has_visual: false,
      victims: [],
      witnesses: [],
      perp_descriptions: [],
      direction_of_flight: "",
      confirmation_status: "",
      transmitted_10_85: false,
      transmitted_10_13: false,
      pd_activated: false,
      ems_required: false,
      is_hotline_call: false,
      is_hate_crime: false,
      dispatch_id: "",
      units_responded: [],
      footage_requested: false,
      footage_obtained: false,
      footage_locations: "",
      job_body: "",
      notes: [],
      additional_locations: [],
      linked_incident_id: "",
      assigned_detective: "",
      hctf_notified: false,
      weapon_displayed: false,
      weapon_notes: "",
      injuries_reported: false,
      injury_notes: "",
      police_followup_required: false,
      police_followup_notes: "",
      police_report_filed: false,
      police_report_notes: "",
      police_action_notes: "",
      needs_followup: false,
      followup_notes: "",
      call_final: "",
    });
    setCallTypeSearch("");
    setSecondaryCallTypeSearch("");
    setQuickUnitSearch("");
    setQuickUnitHighlight(0);
    setNewCallbackNumber({ number: "", caller_type: "", has_visual: false });
    setNewVictim({ name: "", contact: "", showInput: false });
    setNewWitness({ name: "", contact: "", showInput: false });
    resetNewPerp();
    setShowLiveCallOptions(false);
  };

  // Sync weapon data from perps to incident weapon fields
  const syncWeaponFromPerps = (perps) => {
    const hasWeapon = perps.some(p => p.has_weapon);
    const armedPerps = perps.filter(p => p.has_weapon && p.weapon_description);
    let weaponNotes = "";
    if (armedPerps.length > 1) {
      weaponNotes = armedPerps.map((p, i) => {
        const perpIdx = perps.indexOf(p);
        return `Perp #${perpIdx + 1}: ${p.weapon_description}`;
      }).join("\n");
    } else if (armedPerps.length === 1) {
      weaponNotes = armedPerps[0].weapon_description;
    }
    return { weapon_displayed: hasWeapon, weapon_notes: weaponNotes || "" };
  };

  // stayOpen: create the incident, then blank the form and leave the dialog
  // open for the next one instead of navigating to the new incident.
  const handleCreateIncident = async (stayOpen = false) => {
    if (!newIncident.incident_type) {
      toast.error("Please select an incident type");
      return;
    }
    try {
      // Auto-create new incident type if needed
      if (newIncident.incident_type && !incidentTypes.some(t => t.name.toLowerCase() === newIncident.incident_type.toLowerCase())) {
        try {
          await axios.post(`${API}/incident-types`, { name: newIncident.incident_type }, { withCredentials: true });
        } catch (e) {}
      }
      if (newIncident.secondary_incident_type && !incidentTypes.some(t => t.name.toLowerCase() === newIncident.secondary_incident_type.toLowerCase())) {
        try {
          await axios.post(`${API}/incident-types`, { name: newIncident.secondary_incident_type }, { withCredentials: true });
        } catch (e) {}
      }

      // Auto-include typed callback number if not yet added
      const finalIncident = { ...newIncident };
      if (newCallbackNumber.number?.trim()) {
        finalIncident.callback_numbers = [...(finalIncident.callback_numbers || []), { number: newCallbackNumber.number.trim(), caller_type: newCallbackNumber.caller_type, has_visual: newCallbackNumber.has_visual }];
      }

      // Combine date and time into date_time
      const incidentData = { ...finalIncident };
      if (incidentData.date && incidentData.time) {
        incidentData.date_time = new Date(`${incidentData.date}T${incidentData.time}`).toISOString();
      } else if (incidentData.date) {
        incidentData.date_time = new Date(`${incidentData.date}T12:00`).toISOString();
      }
      delete incidentData.date;
      delete incidentData.time;

      // Convert dispatch_id to dispatcher_units array
      if (incidentData.dispatch_id) {
        incidentData.dispatcher_units = [incidentData.dispatch_id];
      }
      delete incidentData.dispatch_id;

      // Add job_body to notes if it has content
      if (incidentData.job_body && incidentData.job_body.trim()) {
        incidentData.notes = [...(incidentData.notes || []), { text: incidentData.job_body.trim(), timestamp: new Date().toISOString(), type: 'job_body' }];
      }
      delete incidentData.job_body;

      const response = await axios.post(`${API}/incidents`, incidentData, { withCredentials: true });
      setIncidents([response.data, ...incidents]);
      const dateJustUsed = newIncident.date;
      resetNewIncidentForm();
      if (stayOpen) {
        // Logging a run of incidents from the same day: carry the date over
        // instead of snapping the next form back to today.
        setNewIncident(prev => ({ ...prev, date: dateJustUsed }));
        toast.success(`Incident ${response.data.incident_id} created — form ready for the next one`);
        return;
      }
      setShowNewDialog(false);
      setShowQuickDialog(false);
      toast.success("Incident created");
      navigate(`/incidents/${response.data.incident_id}`);
    } catch (error) {
      console.error("Create incident error:", error.response?.status, error.response?.data);
      const detail = error.response?.data?.detail;
      toast.error(detail ? `Failed: ${typeof detail === 'string' ? detail : JSON.stringify(detail).slice(0, 100)}` : "Failed to create incident");
    }
  };

  // Incident-type autocomplete for the short form. Same behaviour as the full
  // form's two type inputs (free text allowed, new types auto-created on save),
  // driven by the same search/dropdown state.
  const renderQuickTypeInput = ({ field, search, setSearch, open, setOpen, highlighted, setHighlighted, placeholder }) => {
    const filteredTypes = incidentTypes
      .filter(type => type.name.toLowerCase().includes((search || "").toLowerCase()))
      .sort((a, b) => a.name.localeCompare(b.name));
    const choose = (name) => {
      setNewIncident({ ...newIncident, [field]: name });
      setSearch("");
      setOpen(false);
      setHighlighted(-1);
    };
    return (
      <div className="relative">
        <Input
          value={search || newIncident[field] || ""}
          onChange={(e) => { setSearch(e.target.value); setNewIncident({ ...newIncident, [field]: e.target.value }); setOpen(true); setHighlighted(-1); }}
          onFocus={() => { setOpen(true); setHighlighted(-1); }}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHighlighted(prev => prev < filteredTypes.length - 1 ? prev + 1 : 0); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted(prev => prev > 0 ? prev - 1 : filteredTypes.length - 1); }
            else if ((e.key === 'Enter' || e.key === 'Tab') && highlighted >= 0) { e.preventDefault(); if (filteredTypes[highlighted]) choose(filteredTypes[highlighted].name); }
            else if (e.key === 'Escape') { setOpen(false); setHighlighted(-1); }
          }}
          placeholder={placeholder}
          className="input-dark"
        />
        {open && (search || !newIncident[field]) && (
          <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-md shadow-lg max-h-48 overflow-y-auto">
            {filteredTypes.map((type, idx) => (
              <div
                key={type.type_id}
                className={`px-3 py-2 cursor-pointer text-sm select-none ${idx === highlighted ? 'bg-blue-600 text-white' : 'hover:bg-slate-700'}`}
                onPointerDown={(e) => { e.preventDefault(); choose(type.name); }}
                onMouseEnter={() => setHighlighted(idx)}
              >{type.name}</div>
            ))}
            {search && !incidentTypes.some(t => t.name.toLowerCase() === search.toLowerCase()) && (
              <div className="px-3 py-2 text-xs text-slate-400 border-t border-slate-700">Press Enter to use "{search}"</div>
            )}
          </div>
        )}
      </div>
    );
  };

  const clearFilters = () => {
    setSearchQuery("");
    setFilterType("");
    setFilterStatus("");
    setFilterPriority([]);
    setFilterUnits([]);
    setFilterDispatcher("");
    setFilterArrest(false);
    setFilterArrestVia(false);
    setFilterPR(false);
    setDateRange({ from: null, to: null });
    setFilterHateCrime(false);
    setFilterWeapon(false);
    setFilterPDActivated(false);
    setFilterHasVictims(false);
    setFilterHasWitnesses(false);
    setFilterHasPerps(false);
    setFilterHasMedia(false);
    setFilterNeedsFollowup(false);
    setFilterTransmitted1085(false);
    setFilterTransmitted1013(false);
    setFilterHotline(false);
    setFilterReportType("");
    // New filters
    setFilterConfirmation("");
    setFilterHasAnyProfile(false);
    setFilterSpecificProfile("");
    setFilterReadyForArrest(false);
    setFilterProfileArrested(false);
    setFilterUnitInvestigating("");
    setFilterMinorOrElderly(false);
    setFilterInjuries(false);
    setFilterPoliceFollowup(false);
    setFilterPoliceReport(false);
    setFilterFootageObtained(false);
    setFilterFootageRequested(false);
    setFilterFromLiveCall(false);
    setFilterHasCallbacks(false);
    setFilterCallbackType("");
    // Clear URL params
    setSearchParams({}, { replace: true });
    fetchData();
  };

  // Filter incidents including all filters
  const filteredIncidents = incidents.filter(inc => {
    // Search query filter - expanded to include callback numbers, victims, witnesses, perps, notes
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      // Normalize phone: strip all non-digits for flexible phone matching
      const queryDigits = query.replace(/\D/g, '');
      const isPhoneSearch = queryDigits.length >= 7;
      const phoneMatch = (val) => {
        if (!val) return false;
        if (val.toLowerCase().includes(query)) return true;
        if (isPhoneSearch) {
          const valDigits = val.replace(/\D/g, '');
          return valDigits.includes(queryDigits) || queryDigits.includes(valDigits);
        }
        return false;
      };
      const textMatch = (val) => val?.toLowerCase().includes(query);

      // Basic fields
      const matchesBasic =
        textMatch(inc.headline) ||
        textMatch(inc.incident_id) ||
        textMatch(inc.address) ||
        textMatch(inc.incident_type) ||
        textMatch(inc.secondary_call_type) ||
        textMatch(inc.secondary_incident_type) ||
        textMatch(inc.description);

      // Callback numbers search
      const matchesCallback = inc.callback_numbers?.some(cb =>
        phoneMatch(cb.number) ||
        textMatch(cb.label)
      ) || false;

      // Victim info search - all fields
      const matchesVictims = inc.victims?.some(v =>
        textMatch(v.name) ||
        phoneMatch(v.phone1) ||
        phoneMatch(v.phone2) ||
        textMatch(v.address) ||
        textMatch(v.email) ||
        textMatch(v.statement) ||
        textMatch(v.property_stolen) ||
        textMatch(v.injuries_description) ||
        textMatch(v.medical_treatment) ||
        textMatch(v.direction_of_flight_prior) ||
        textMatch(v.direction_of_flight_post) ||
        textMatch(v.response) ||
        textMatch(v.guardian_name) ||
        textMatch(v.guardian_email) ||
        textMatch(v.guardian_address) ||
        phoneMatch(v.guardian_phone1) ||
        phoneMatch(v.guardian_phone2) ||
        textMatch(v.guardian_statement)
      ) || false;

      // Witness info search - all fields
      const matchesWitnesses = inc.witnesses?.some(w =>
        textMatch(w.name) ||
        phoneMatch(w.phone1) ||
        phoneMatch(w.phone2) ||
        textMatch(w.contact_info) ||
        textMatch(w.location) ||
        textMatch(w.statement) ||
        textMatch(w.response)
      ) || false;

      // Perp descriptions + linked profile search
      const matchesPerps = inc.perp_descriptions?.some(p => {
        // Search perp fields
        if (textMatch(p.name) || textMatch(p.description) || textMatch(p.clothing) ||
            textMatch(p.direction_of_flight) || textMatch(p.linked_profile_name) ||
            textMatch(p.address) || textMatch(p.vehicle_description) ||
            textMatch(p.license_plate) || textMatch(p.notes)) return true;
        // Resolve linked profile name from profiles list
        if (p.linked_profile_id) {
          const prof = profiles.find(pr => pr.profile_id === p.linked_profile_id);
          if (prof && (textMatch(prof.name) || textMatch(prof.known_address) || textMatch(prof.aliases) || textMatch(prof.affiliates) || textMatch(prof.notes))) return true;
        }
        return false;
      }) || false;

      // Also check incident-level linked_profiles array
      const matchesLinkedProfiles = (inc.linked_profiles || []).some(pid => {
        const prof = profiles.find(pr => pr.profile_id === pid);
        return prof && (textMatch(prof.name) || textMatch(prof.known_address) || textMatch(prof.aliases) || textMatch(prof.affiliates) || textMatch(prof.notes));
      });

      // Notes search
      const matchesNotes = inc.notes?.some(n =>
        (typeof n === 'string' && n.toLowerCase().includes(query)) ||
        (typeof n === 'object' && n.text?.toLowerCase().includes(query))
      ) || false;

      // Apply search field filter
      let matches = false;
      const matchesUnits = inc.units_responded?.some(u => u.toLowerCase().includes(query)) || false;
      const matchesDispatchers = inc.dispatcher_units?.some(u => u.toLowerCase().includes(query)) || false;

      if (searchField === "all") {
        matches = matchesBasic || matchesCallback || matchesVictims || matchesWitnesses || matchesPerps || matchesLinkedProfiles || matchesNotes || matchesUnits || matchesDispatchers;
      } else if (searchField === "headline") {
        matches = textMatch(inc.headline);
      } else if (searchField === "id") {
        matches = textMatch(inc.incident_id);
      } else if (searchField === "address") {
        matches = textMatch(inc.address) || textMatch(inc.cross_streets) || textMatch(inc.location_notes);
      } else if (searchField === "callback") {
        matches = matchesCallback;
      } else if (searchField === "victim") {
        matches = matchesVictims;
      } else if (searchField === "witness") {
        matches = matchesWitnesses;
      } else if (searchField === "perp") {
        matches = matchesPerps || matchesLinkedProfiles;
      } else if (searchField === "description") {
        matches = textMatch(inc.description) || textMatch(inc.live_call_description);
      } else if (searchField === "notes") {
        matches = matchesNotes;
      } else if (searchField === "unit") {
        matches = inc.units_responded?.some(u => u.toLowerCase().includes(query)) || false;
      } else if (searchField === "dispatcher") {
        matches = inc.dispatcher_units?.some(u => u.toLowerCase().includes(query)) || false;
      } else {
        matches = matchesBasic || matchesCallback || matchesVictims || matchesWitnesses || matchesPerps || matchesNotes;
      }
      if (!matches) return false;
    }
    
    // Type filter
    if (filterType && filterType !== "all" && inc.incident_type !== filterType && inc.secondary_call_type !== filterType && inc.secondary_incident_type !== filterType) return false;
    
    // Status filter
    if (filterStatus && filterStatus !== "all" && inc.status !== filterStatus) return false;
    
    // Units filter (responded) - multiple units - ALL selected units must be on the incident
    if (filterUnits.length > 0) {
      const hasAllUnits = filterUnits.every(unit => 
        inc.units_responded?.includes(unit) || inc.units_investigating?.includes(unit)
      );
      if (!hasAllUnits) return false;
    }
    
    // Unit investigating filter
    if (filterUnitInvestigating && filterUnitInvestigating !== "all") {
      if (!inc.units_investigating?.includes(filterUnitInvestigating)) return false;
    }
    
    // Dispatcher filter
    if (filterDispatcher && filterDispatcher !== "all") {
      const hasDispatcher = inc.dispatcher_units?.includes(filterDispatcher);
      if (!hasDispatcher) return false;
    }
    
    // Arrest filter - check incident status, perp was_arrested, AND linked profile arrests
    if (filterArrest) {
      const hasIncidentArrest = inc.is_arrest === true || inc.status === "arrest";
      const hasPerpArrest = inc.perp_descriptions?.some(p => p.was_arrested === true);
      
      // Also check if any linked profile has arrests
      const linkedProfileIds = inc.perp_descriptions?.filter(p => p.linked_profile_id).map(p => p.linked_profile_id) || [];
      const linkedFromIncident = inc.linked_profiles || [];
      const allLinkedProfiles = [...new Set([...linkedProfileIds, ...linkedFromIncident])];
      const hasLinkedProfileArrest = allLinkedProfiles.some(profileId => {
        const profile = profiles.find(p => p.profile_id === profileId);
        return profile?.arrest_count > 0 || profile?.arrests?.length > 0;
      });

      if (!hasIncidentArrest && !hasPerpArrest && !hasLinkedProfileArrest) return false;
    }

    // Arrest VIA filter — only incidents that earn the ARREST VIA badge.
    // Backend computes is_arrest_via with the Profiles-page semantics:
    // this incident's Ready-for-Arrest was consumed by an arrest on a
    // *different* incident, AND there's no arrest record on this incident
    // itself (a real arrest here gets the regular ARREST badge instead).
    if (filterArrestVia && !inc.is_arrest_via) return false;

    // PR filter
    if (filterPR && !inc.is_pr) return false;
    
    // Date range filter
    if (dateRange.from || dateRange.to) {
      const incDate = inc.date_time ? new Date(inc.date_time) : null;
      if (incDate) {
        if (dateRange.from && incDate < dateRange.from) return false;
        if (dateRange.to && incDate > new Date(dateRange.to.getTime() + 86400000)) return false;
      }
    }
    
    // Additional filters
    if (filterHateCrime && !inc.is_hate_crime) return false;
    if (filterWeapon && !inc.weapon_displayed) return false;
    if (filterPDActivated && !inc.pd_activated) return false;
    if (filterHasVictims && (!inc.victims || inc.victims.length === 0)) return false;
    if (filterHasWitnesses && (!inc.witnesses || inc.witnesses.length === 0)) return false;
    if (filterHasPerps && (!inc.perp_descriptions || inc.perp_descriptions.length === 0)) return false;
    if (filterHasMedia && !inc.has_media) return false;
    if (filterNeedsFollowup && !inc.needs_followup) return false;
    if (filterTransmitted1085 && !inc.transmitted_10_85) return false;
    if (filterTransmitted1013 && !inc.transmitted_10_13) return false;
    if (filterHotline && !inc.is_hotline_call) return false;
    if (filterReportType && filterReportType !== "all") {
      if (filterReportType === "reported_after") {
        if (inc.report_type !== "reported_after" && inc.report_type !== "reported_in_past") return false;
      } else {
        if (inc.report_type !== filterReportType) return false;
      }
    }
    
    // New filters
    // Confirmation status
    if (filterConfirmation && filterConfirmation !== "all" && inc.confirmation_status !== filterConfirmation) return false;
    
    // Has any profile attached
    if (filterHasAnyProfile) {
      const hasProfile = inc.perp_descriptions?.some(p => p.linked_profile_id) || inc.linked_profiles?.length > 0;
      if (!hasProfile) return false;
    }
    
    // Specific profile
    if (filterSpecificProfile && filterSpecificProfile !== "all") {
      const hasSpecificProfile = inc.perp_descriptions?.some(p => p.linked_profile_id === filterSpecificProfile) || 
                                  inc.linked_profiles?.includes(filterSpecificProfile);
      if (!hasSpecificProfile) return false;
      
      // If filtering for arrested AND a specific profile is selected, 
      // check if THIS specific profile has arrests
      if (filterProfileArrested) {
        const profile = profiles.find(p => p.profile_id === filterSpecificProfile);
        const specificProfileHasArrests = profile?.arrest_count > 0 || profile?.arrests?.length > 0;
        if (!specificProfileHasArrests) return false;
      }
    }
    
    // Ready for arrest (has perp identified for arrest)
    if (filterReadyForArrest) {
      const hasIdentified = inc.perp_descriptions?.some(p => p.identified_for_arrest === true);
      if (!hasIdentified) return false;
    }
    
    // Profile arrested (has perp with linked profile that has arrests)
    // Only apply this filter if no specific profile is selected (otherwise handled above)
    if (filterProfileArrested && (!filterSpecificProfile || filterSpecificProfile === "all")) {
      const linkedProfileIds = inc.perp_descriptions?.filter(p => p.linked_profile_id).map(p => p.linked_profile_id) || [];
      const linkedFromIncident = inc.linked_profiles || [];
      const allLinkedProfiles = [...new Set([...linkedProfileIds, ...linkedFromIncident])];
      const hasArrestedProfile = allLinkedProfiles.some(profileId => {
        const profile = profiles.find(p => p.profile_id === profileId);
        return profile?.arrest_count > 0 || profile?.arrests?.length > 0;
      });
      if (!hasArrestedProfile) return false;
    }
    
    // Minor or elderly victim
    if (filterMinorOrElderly) {
      const hasMinorElderly = inc.victims?.some(v => v.is_minor_or_elderly === true);
      if (!hasMinorElderly) return false;
    }
    
    // Injuries
    if (filterInjuries) {
      const hasInjuries = inc.injuries_reported === true || inc.victims?.some(v => v.has_injuries === true);
      if (!hasInjuries) return false;
    }
    
    // Police followup required
    if (filterPoliceFollowup && !inc.police_followup_required) return false;
    
    // Police report filed
    if (filterPoliceReport) {
      if (!inc.police_report_filed) return false;
    }
    
    // Footage obtained
    if (filterFootageObtained && !inc.footage_obtained) return false;

    // Footage requested
    if (filterFootageRequested && !inc.footage_requested) return false;

    // From live call
    if (filterFromLiveCall && !inc.from_live_call_id) return false;

    // Has callbacks
    if (filterHasCallbacks && (!inc.callback_numbers || inc.callback_numbers.length === 0)) return false;

    // Callback type filter
    if (filterCallbackType) {
      const cbs = inc.callback_numbers || [];
      if (cbs.length === 0) return false;
      if (filterCallbackType === "cv") {
        if (!cbs.some(cb => (cb.caller_type || cb.type) === "cv")) return false;
      } else if (filterCallbackType === "3rd_party") {
        if (!cbs.some(cb => (cb.caller_type || cb.type) === "3rd_party")) return false;
      } else if (filterCallbackType === "none") {
        if (!cbs.some(cb => !(cb.caller_type || cb.type))) return false;
      }
    }

    return true;
  });

  // Sort filtered incidents
  const handleColumnSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection(field === "date" ? "desc" : "asc");
    }
  };

  const sortedIncidents = [...filteredIncidents].sort((a, b) => {
    let valA, valB;
    switch (sortField) {
      case "id":
        valA = a.incident_id || ""; valB = b.incident_id || "";
        return sortDirection === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
      case "headline":
        valA = a.headline || ""; valB = b.headline || "";
        return sortDirection === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
      case "type":
        valA = a.incident_type || ""; valB = b.incident_type || "";
        return sortDirection === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
      case "location":
        valA = a.address || ""; valB = b.address || "";
        return sortDirection === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
      case "status":
        valA = a.status || ""; valB = b.status || "";
        return sortDirection === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
      case "priority":
        const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
        valA = priorityOrder[a.priority] ?? 4; valB = priorityOrder[b.priority] ?? 4;
        return sortDirection === "asc" ? valA - valB : valB - valA;
      case "created":
        valA = new Date(a.created_at || a.date_time);
        valB = new Date(b.created_at || b.date_time);
        return sortDirection === "asc" ? valA - valB : valB - valA;
      case "date":
      default:
        valA = new Date(a.date_time || a.created_at);
        valB = new Date(b.date_time || b.created_at);
        return sortDirection === "asc" ? valA - valB : valB - valA;
    }
  });

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sortedIncidents.length / perPage));
  const paginatedIncidents = sortedIncidents.slice((currentPage - 1) * perPage, currentPage * perPage);

  // Reset page when filters change
  const skipPageResetRef = useRef(false);
  useEffect(() => {
    if (skipPageResetRef.current) {
      skipPageResetRef.current = false;
      return;
    }
    setCurrentPage(1);
  }, [filteredIncidents.length, perPage]);

  const renderPaginationCompact = () => (
    sortedIncidents.length > 0 ? (
      <div className="flex items-center gap-1.5 ml-auto">
        <span className="text-[10px] text-slate-500 hidden sm:inline">
          {Math.min((currentPage - 1) * perPage + 1, sortedIncidents.length)}-{Math.min(currentPage * perPage, sortedIncidents.length)}/{sortedIncidents.length}
        </span>
        {totalPages > 1 && (
          <>
            <Button variant="outline" size="sm" className="h-6 w-6 p-0" disabled={currentPage <= 1} onClick={() => setCurrentPage(currentPage - 1)}>
              <ChevronLeft className="h-3 w-3" />
            </Button>
            <span className="text-[10px] text-slate-400">{currentPage}/{totalPages}</span>
            <Button variant="outline" size="sm" className="h-6 w-6 p-0" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
              <ChevronRight className="h-3 w-3" />
            </Button>
          </>
        )}
        <select
          value={perPage}
          onChange={(e) => setPerPage(Number(e.target.value))}
          title="Per page"
          className="h-6 rounded border border-slate-700 bg-slate-800 px-1 text-[10px] text-slate-300"
        >
          {[25, 50, 100, 200].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
    ) : null
  );

  const renderPagination = () => (
    sortedIncidents.length > 0 ? (
      <div className="flex items-center justify-between flex-wrap gap-2 py-2">
        <span className="text-xs text-slate-500">
          Showing {Math.min((currentPage - 1) * perPage + 1, sortedIncidents.length)}-{Math.min(currentPage * perPage, sortedIncidents.length)} of {sortedIncidents.length}
        </span>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-slate-500 mr-1">Per page:</span>
          {[25, 50, 100, 200].map((n) => (
            <button
              key={n}
              onClick={() => setPerPage(n)}
              className={`px-1.5 py-0.5 text-xs rounded ${perPage === n ? "bg-blue-600 text-white" : "bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-300 dark:hover:bg-slate-700"}`}
            >{n}</button>
          ))}
        </div>
        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={currentPage <= 1} onClick={() => setCurrentPage(currentPage - 1)}>
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                acc.push(p);
                return acc;
              }, [])
              .map((p, idx) =>
                p === "..." ? (
                  <span key={`dots-${idx}`} className="text-xs text-slate-500 px-1">...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`h-7 min-w-[28px] px-1.5 text-xs rounded ${currentPage === p ? "bg-blue-600 text-white" : "bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-300 dark:hover:bg-slate-700"}`}
                  >{p}</button>
                )
              )}
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>
    ) : null
  );

  const renderPhoneNumbers = (incident, size = "xs") => {
    if (!showPhoneNumbers) return null;
    const phones = [];
    incident.callback_numbers?.forEach(cb => {
      if (cb.number) phones.push({ type: "CB", number: cb.number, color: "text-amber-400" });
    });
    incident.victims?.forEach(v => {
      if (v.phone1) phones.push({ type: "V", number: v.phone1, color: "text-blue-400" });
      if (v.phone2) phones.push({ type: "V", number: v.phone2, color: "text-blue-400" });
    });
    incident.witnesses?.forEach(w => {
      if (w.contact_info) phones.push({ type: "W", number: w.contact_info, color: "text-green-400" });
    });
    if (phones.length === 0) return null;
    const textSize = size === "9" ? "text-[9px]" : "text-xs";
    return (
      <div className={`flex flex-wrap gap-x-2 gap-y-0.5 ${textSize} font-mono`}>
        {phones.map((p, i) => (
          <span key={i} className="whitespace-nowrap">
            <span className={`${p.color} font-semibold`}>{p.type}:</span>{" "}
            <span className="text-slate-300">{p.number}</span>
          </span>
        ))}
      </div>
    );
  };

  const getStatusClass = (status) => {
    return `status-badge status-${status}`;
  };

  const getPriorityClass = (priority) => {
    return `priority-${priority}`;
  };

  if (!hasAccess) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <Card>
          <CardContent className="pt-6 text-center">
            <Shield className="h-12 w-12 mx-auto mb-4 text-slate-500" />
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
            <p className="text-slate-400 text-sm">
              You do not have permission to view incidents.
              Contact an administrator to request access.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="incidents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
          <h1 className="text-xl sm:text-2xl font-bold uppercase tracking-tight">Incidents</h1>
          <Badge variant="outline" className="text-xs">{incidents.length}</Badge>
          <Select value={`${sortField}_${sortDirection}`} onValueChange={(v) => {
            const [field, dir] = v.split("_");
            setSortField(field);
            setSortDirection(dir);
          }}>
            <SelectTrigger className="w-28 sm:w-32 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date_desc">Newest</SelectItem>
              <SelectItem value="date_asc">Oldest</SelectItem>
              <SelectItem value="created_desc">Created Newest</SelectItem>
              <SelectItem value="created_asc">Created Oldest</SelectItem>
              <SelectItem value="id_asc">ID A-Z</SelectItem>
              <SelectItem value="id_desc">ID Z-A</SelectItem>
              <SelectItem value="type_asc">Type A-Z</SelectItem>
              <SelectItem value="type_desc">Type Z-A</SelectItem>
              <SelectItem value="location_asc">Location A-Z</SelectItem>
              <SelectItem value="location_desc">Location Z-A</SelectItem>
              <SelectItem value="status_asc">Status A-Z</SelectItem>
              <SelectItem value="status_desc">Status Z-A</SelectItem>
              <SelectItem value="priority_asc">Priority High</SelectItem>
              <SelectItem value="priority_desc">Priority Low</SelectItem>
              <SelectItem value="headline_asc">Headline A-Z</SelectItem>
              <SelectItem value="headline_desc">Headline Z-A</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Dialog open={showNewDialog} onOpenChange={(open) => {
              if (!open) {
                if (showPerpDialog || showExtraDetails) return;
                resetNewIncidentForm();
              }
              setShowNewDialog(open);
            }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" size="sm" data-testid="new-incident-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </DialogTrigger>
            {/* Second + button: the short form */}
            <Button
              variant="outline"
              size="sm"
              className="border-slate-600 text-slate-200 hover:bg-slate-800 gap-1 px-2"
              data-testid="quick-incident-btn"
              title="New incident — short form"
              onClick={() => { resetNewIncidentForm(); setShowQuickDialog(true); }}
            >
              <Plus className="w-4 h-4" />
              <span className="text-[10px] uppercase tracking-wider">Quick</span>
            </Button>
            {/* Mobile view toggle */}
            <div className="md:hidden flex items-center gap-1 bg-slate-800 rounded p-0.5">
              <button onClick={() => { setMobileViewMode("cards"); localStorage.setItem("incidents_mobile_view", "cards"); }} className={`p-1 rounded transition-colors ${mobileViewMode === "cards" ? "bg-slate-600 text-white" : "text-slate-400 hover:text-white"}`} title="Card view">
                <LayoutGrid className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => { setMobileViewMode("compact"); localStorage.setItem("incidents_mobile_view", "compact"); }} className={`p-1 rounded transition-colors ${mobileViewMode === "compact" ? "bg-slate-600 text-white" : "text-slate-400 hover:text-white"}`} title="Compact view">
                <List className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => { setMobileViewMode("map"); localStorage.setItem("incidents_mobile_view", "map"); }} className={`p-1 rounded transition-colors ${mobileViewMode === "map" ? "bg-slate-600 text-white" : "text-slate-400 hover:text-white"}`} title="Map view">
                <MapPinned className="w-3.5 h-3.5" />
              </button>
            </div>
            <DialogContent
              className={`w-[calc(100vw-2rem)] max-w-lg bg-slate-900 border-slate-800 max-h-[90vh] overflow-hidden flex flex-col p-0 ${showPerpDialog ? "opacity-0 pointer-events-none" : ""}`}
              onPointerDownOutside={(e) => { if (showPerpDialog || showExtraDetails) e.preventDefault(); }}
              onInteractOutside={(e) => { if (showPerpDialog || showExtraDetails) e.preventDefault(); }}
              onEscapeKeyDown={(e) => { if (showPerpDialog || showExtraDetails) e.preventDefault(); }}
            >
              <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
                <DialogTitle className="uppercase tracking-wider">New Incident</DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto overflow-x-hidden px-6">
                <div className="space-y-4 mt-4 pb-4">
                  {/* Priority and Incident Type */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Priority</Label>
                      <Select value={newIncident.priority} onValueChange={(v) => setNewIncident({ ...newIncident, priority: v })}>
                        <SelectTrigger className="input-dark" data-testid="priority-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Incident Type *</Label>
                      <div className="relative">
                        <Input
                          value={callTypeSearch || newIncident.incident_type}
                          onChange={(e) => {
                            setCallTypeSearch(e.target.value);
                            setNewIncident({ ...newIncident, incident_type: e.target.value });
                            setShowCallTypeDropdown(true);
                            setCallTypeHighlightedIndex(-1);
                          }}
                          onFocus={() => { setShowCallTypeDropdown(true); setCallTypeHighlightedIndex(-1); }}
                          onBlur={() => setTimeout(() => setShowCallTypeDropdown(false), 200)}
                          onKeyDown={(e) => {
                            const filteredTypes = incidentTypes.filter(type => type.name.toLowerCase().includes((callTypeSearch || "").toLowerCase())).sort((a, b) => a.name.localeCompare(b.name));
                            if (e.key === 'ArrowDown') { e.preventDefault(); setShowCallTypeDropdown(true); setCallTypeHighlightedIndex(prev => prev < filteredTypes.length - 1 ? prev + 1 : 0); }
                            else if (e.key === 'ArrowUp') { e.preventDefault(); setCallTypeHighlightedIndex(prev => prev > 0 ? prev - 1 : filteredTypes.length - 1); }
                            else if ((e.key === 'Enter' || e.key === 'Tab') && callTypeHighlightedIndex >= 0) {
                              e.preventDefault();
                              const selectedType = filteredTypes[callTypeHighlightedIndex];
                              if (selectedType) { setNewIncident({ ...newIncident, incident_type: selectedType.name }); setCallTypeSearch(""); setShowCallTypeDropdown(false); setCallTypeHighlightedIndex(-1); }
                            } else if (e.key === 'Escape') { setShowCallTypeDropdown(false); setCallTypeHighlightedIndex(-1); }
                          }}
                          placeholder="Search or enter type..."
                          className="input-dark"
                          data-testid="type-select"
                        />
                        {showCallTypeDropdown && (callTypeSearch || !newIncident.incident_type) && (
                          <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-md shadow-lg max-h-48 overflow-y-auto">
                            {incidentTypes.filter(type => type.name.toLowerCase().includes((callTypeSearch || "").toLowerCase())).sort((a, b) => a.name.localeCompare(b.name)).map((type, idx) => (
                              <div key={type.type_id} className={`px-3 py-2 cursor-pointer text-sm select-none ${idx === callTypeHighlightedIndex ? 'bg-blue-600 text-white' : 'hover:bg-slate-700'}`}
                                onPointerDown={(e) => { e.preventDefault(); setNewIncident({ ...newIncident, incident_type: type.name }); setCallTypeSearch(""); setShowCallTypeDropdown(false); setCallTypeHighlightedIndex(-1); }}
                                onMouseEnter={() => setCallTypeHighlightedIndex(idx)}
                              >{type.name}</div>
                            ))}
                            {callTypeSearch && !incidentTypes.some(t => t.name.toLowerCase() === callTypeSearch.toLowerCase()) && (
                              <div className="px-3 py-2 text-xs text-slate-400 border-t border-slate-700">Press Enter to use "{callTypeSearch}"</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Secondary Incident Type */}
                  <div>
                    <Label>Secondary Type (Optional)</Label>
                    <div className="relative">
                      <Input
                        value={secondaryCallTypeSearch || newIncident.secondary_incident_type}
                        onChange={(e) => {
                          setSecondaryCallTypeSearch(e.target.value);
                          setNewIncident({ ...newIncident, secondary_incident_type: e.target.value });
                          setShowSecondaryCallTypeDropdown(true);
                          setSecondaryCallTypeHighlightedIndex(-1);
                        }}
                        onFocus={() => { setShowSecondaryCallTypeDropdown(true); setSecondaryCallTypeHighlightedIndex(-1); }}
                        onBlur={() => setTimeout(() => setShowSecondaryCallTypeDropdown(false), 200)}
                        onKeyDown={(e) => {
                          const filteredTypes = incidentTypes.filter(type => type.name.toLowerCase().includes((secondaryCallTypeSearch || "").toLowerCase())).sort((a, b) => a.name.localeCompare(b.name));
                          if (e.key === 'ArrowDown') { e.preventDefault(); setShowSecondaryCallTypeDropdown(true); setSecondaryCallTypeHighlightedIndex(prev => prev < filteredTypes.length - 1 ? prev + 1 : 0); }
                          else if (e.key === 'ArrowUp') { e.preventDefault(); setSecondaryCallTypeHighlightedIndex(prev => prev > 0 ? prev - 1 : filteredTypes.length - 1); }
                          else if (e.key === 'Enter' && secondaryCallTypeHighlightedIndex >= 0) {
                            e.preventDefault();
                            const selectedType = filteredTypes[secondaryCallTypeHighlightedIndex];
                            if (selectedType) { setNewIncident({ ...newIncident, secondary_incident_type: selectedType.name }); setSecondaryCallTypeSearch(""); setShowSecondaryCallTypeDropdown(false); setSecondaryCallTypeHighlightedIndex(-1); }
                          } else if (e.key === 'Escape') { setShowSecondaryCallTypeDropdown(false); setSecondaryCallTypeHighlightedIndex(-1); }
                        }}
                        placeholder="Search or enter secondary type..."
                        className="input-dark"
                      />
                      {showSecondaryCallTypeDropdown && (secondaryCallTypeSearch || !newIncident.secondary_incident_type) && (
                        <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-md shadow-lg max-h-48 overflow-y-auto">
                          {incidentTypes.filter(type => type.name.toLowerCase().includes((secondaryCallTypeSearch || "").toLowerCase())).sort((a, b) => a.name.localeCompare(b.name)).map((type, idx) => (
                            <div key={type.type_id} className={`px-3 py-2 cursor-pointer text-sm select-none ${idx === secondaryCallTypeHighlightedIndex ? 'bg-blue-600 text-white' : 'hover:bg-slate-700'}`}
                              onPointerDown={(e) => { e.preventDefault(); setNewIncident({ ...newIncident, secondary_incident_type: type.name }); setSecondaryCallTypeSearch(""); setShowSecondaryCallTypeDropdown(false); setSecondaryCallTypeHighlightedIndex(-1); }}
                              onMouseEnter={() => setSecondaryCallTypeHighlightedIndex(idx)}
                            >{type.name}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Date and Time */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={newIncident.date || new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' })}
                        onChange={(e) => setNewIncident({ ...newIncident, date: e.target.value })}
                        className="input-dark"
                      />
                    </div>
                    <div>
                      <Label>Time</Label>
                      <Input
                        type="time"
                        value={newIncident.time || new Date().toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false })}
                        onChange={(e) => setNewIncident({ ...newIncident, time: e.target.value })}
                        className="input-dark"
                      />
                    </div>
                  </div>

                  {/* Location Settings */}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1.5">
                      <Checkbox
                        id="enableLocationPredictions"
                        checked={enableLocationPredictions}
                        onCheckedChange={setEnableLocationPredictions}
                        className="h-3 w-3"
                      />
                      <label htmlFor="enableLocationPredictions" className="cursor-pointer text-xs text-slate-400">
                        Auto-suggest
                      </label>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Checkbox
                        id="enableGoogleLookup"
                        checked={enableGoogleLookup}
                        onCheckedChange={setEnableGoogleLookup}
                        className="h-3 w-3"
                      />
                      <label htmlFor="enableGoogleLookup" className="text-xs text-slate-400 cursor-pointer">
                        Auto-lookup cross streets
                      </label>
                    </div>
                  </div>

                  {/* Original Location — always-visible inline field (the
                      address input is shown directly rather than hidden behind
                      a "Set Location" button). The button now only opens the
                      dialog for the optional extras: title, notes, cross
                      streets, direction of flight. */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label>Location *</Label>
                      <Button type="button" variant="outline" size="sm" className="h-6 text-xs" onClick={() => {
                        setAddLocData({
                          title: newIncident.location_title || "",
                          location: newIncident.address || "",
                          location_notes: newIncident.location_notes || "",
                          cross_streets: newIncident.cross_streets || "",
                          direction_of_flight: newIncident.direction_of_flight || "",
                        });
                        setEditingLocIndex("original");
                        setShowAddLocDialog(true);
                      }}>
                        <Edit className="w-3 h-3 mr-1" />Details
                      </Button>
                    </div>
                    <div className="relative">
                      <Input
                        value={newIncident.address || ""}
                        onChange={(e) => handleAddressChange(e.target.value)}
                        onFocus={() => { if (enableLocationPredictions && locationPredictions.length > 0) setShowLocationPredictions(true); }}
                        onBlur={() => { setTimeout(() => setShowLocationPredictions(false), 200); handleAddressBlur(); }}
                        placeholder="Enter location..."
                        className="input-dark"
                        autoComplete="off"
                        data-testid="incident-location-input"
                      />
                      {showLocationPredictions && locationPredictions.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-md shadow-lg max-h-48 overflow-y-auto">
                          {locationPredictions.map((prediction, idx) => (
                            <div
                              key={prediction.place_id || idx}
                              className="px-3 py-2 cursor-pointer text-sm hover:bg-slate-700 border-b border-slate-700/50 last:border-0"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => selectLocationPrediction(prediction)}
                            >
                              <div className="font-medium text-slate-200">{prediction.main_text}</div>
                              {prediction.secondary_text && <div className="text-xs text-slate-400">{prediction.secondary_text}</div>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Summary of the optional extras set via the Details dialog */}
                    {(newIncident.location_title || newIncident.cross_streets || newIncident.location_notes || newIncident.direction_of_flight) && (
                      <div className="mt-1.5 ml-1 space-y-0.5">
                        {newIncident.location_title && <p className="text-xs text-amber-400 font-semibold uppercase">{newIncident.location_title}</p>}
                        {newIncident.cross_streets && <p className="text-xs text-emerald-400">✕ {newIncident.cross_streets}</p>}
                        {newIncident.location_notes && <p className="text-xs text-slate-400 italic">{newIncident.location_notes}</p>}
                        {newIncident.direction_of_flight && <p className="text-xs text-amber-400">→ {newIncident.direction_of_flight}</p>}
                      </div>
                    )}
                  </div>

                  {/* Additional Locations */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs text-slate-400 uppercase">Additional Locations ({(newIncident.additional_locations || []).length})</Label>
                      <Button type="button" variant="outline" size="sm" className="h-6 text-xs" onClick={() => openAddLocDialog()}>
                        <Plus className="w-3 h-3 mr-1" />Add Location
                      </Button>
                    </div>
                    {(newIncident.additional_locations || []).map((loc, idx) => (
                      <div key={idx} className="p-2 bg-blue-950/30 border border-blue-800/40 rounded mb-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            {loc.title && <p className="text-xs text-blue-300 font-semibold uppercase mb-0.5">{loc.title}</p>}
                            <div className="flex items-center gap-1">
                              <MapPin className="w-3 h-3 text-blue-400 shrink-0" />
                              <span className="text-sm font-medium text-slate-200 truncate">{loc.location}</span>
                            </div>
                            {loc.cross_streets && <p className="text-xs text-emerald-400 ml-4">✕ {loc.cross_streets}</p>}
                            {loc.location_notes && <p className="text-xs text-slate-400 ml-4 italic">{loc.location_notes}</p>}
                            {loc.direction_of_flight && <p className="text-xs text-amber-400 ml-4">→ {loc.direction_of_flight}</p>}
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-blue-400" onClick={() => openAddLocDialog(idx)}>
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => setDeleteLocIndex(idx)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Headline */}
                  <div>
                    <Label>Headline (auto-generated if blank)</Label>
                    <Input
                      value={newIncident.headline}
                      onChange={(e) => setNewIncident({ ...newIncident, headline: e.target.value })}
                      placeholder="Brief incident headline..."
                      className="input-dark"
                      data-testid="headline-input"
                    />
                  </div>

                  {/* Callback Numbers */}
                  <div className="space-y-2">
                    <Label>Callback Numbers</Label>
                    <div className="flex gap-2 items-center flex-wrap">
                      <Input
                        value={newCallbackNumber.number}
                        onChange={(e) => setNewCallbackNumber({ ...newCallbackNumber, number: formatPhoneNumber(e.target.value) })}
                        placeholder="Phone number..."
                        className="input-dark flex-1 min-w-[120px]"
                        type="tel"
                        inputMode="numeric"
                      />
                      <Button type="button" variant="outline" size="sm" title="Paste from clipboard" onClick={async () => {
                        try { const text = await navigator.clipboard.readText(); if (text) setNewCallbackNumber({ ...newCallbackNumber, number: formatPhoneNumber(text.trim()) }); }
                        catch (e) { toast.error("Could not access clipboard"); }
                      }} className="h-8 px-2"><ClipboardPaste className="w-4 h-4" /></Button>
                      <Popover open={showRecentCalls} onOpenChange={(open) => { setShowRecentCalls(open); if (open) fetchRecentIncomingCalls(); }}>
                        <PopoverTrigger asChild>
                          <Button type="button" variant="outline" size="sm" title="Recent incoming calls (30 min)" className="h-8 px-2"><PhoneIncoming className="w-4 h-4" /></Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72 p-2 bg-slate-900 border-slate-700 z-[9999]">
                          <div className="text-xs text-slate-400 mb-2">Recent Incoming Calls (30 min)</div>
                          {loadingRecentCalls ? (
                            <div className="text-center py-2"><RefreshCw className="w-4 h-4 animate-spin mx-auto" /></div>
                          ) : recentIncomingCalls.length === 0 ? (
                            <div className="text-xs text-slate-500 text-center py-2">No recent calls</div>
                          ) : (
                            <div className="space-y-1 max-h-48 overflow-y-auto">
                              {recentIncomingCalls.map((call, idx) => (
                                <Button key={idx} variant="ghost" size="sm" className="w-full justify-between text-xs h-8 px-2" onClick={() => { setNewCallbackNumber({ ...newCallbackNumber, number: call.caller_number }); setShowRecentCalls(false); }}>
                                  <span className="flex items-center gap-1.5 truncate"><Phone className="w-3 h-3 shrink-0" />{call.caller_number}{call.contact_name && <span className="text-slate-400 truncate">{call.contact_name}</span>}</span>
                                  <span className="flex items-center gap-1 shrink-0 ml-1">
                                    <span className="text-[10px] text-slate-500">{formatCallTime(call.received_at)}</span>
                                    {call.contact_status === "flagged" && <Flag className="w-3 h-3 text-yellow-400" />}
                                    {call.contact_status === "known" && <span className="text-[10px] font-bold text-blue-400 bg-blue-400/20 rounded px-1">K</span>}
                                    {call.contact_status && !["flagged","known"].includes(call.contact_status) && <span className="text-[9px] text-slate-500 uppercase">{call.contact_status.slice(0,3)}</span>}
                                  </span>
                                </Button>
                              ))}
                            </div>
                          )}
                        </PopoverContent>
                      </Popover>
                      {newCallbackNumber.number && (
                        <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-blue-400" title="Lookup phone number" onClick={() => setPhoneLookupNumber(newCallbackNumber.number)}>
                          <Search className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Select value={newCallbackNumber.caller_type || ""} onValueChange={(v) => setNewCallbackNumber({ ...newCallbackNumber, caller_type: v })}>
                        <SelectTrigger className="input-dark w-[70px]"><SelectValue placeholder="Type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cv">CV</SelectItem>
                          <SelectItem value="3rd_party">3rd</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex items-center gap-1">
                        <Checkbox id="cb_visual" checked={newCallbackNumber.has_visual || false} onCheckedChange={(checked) => setNewCallbackNumber({ ...newCallbackNumber, has_visual: checked })} />
                        <Label htmlFor="cb_visual" className="text-xs cursor-pointer whitespace-nowrap">Vis</Label>
                      </div>
                      <Button type="button" variant="outline" size="sm" onClick={() => {
                        if (newCallbackNumber.number.trim()) {
                          setNewIncident({ ...newIncident, callback_numbers: [...(newIncident.callback_numbers || []), { number: newCallbackNumber.number, caller_type: newCallbackNumber.caller_type, has_visual: newCallbackNumber.has_visual }] });
                          setNewCallbackNumber({ number: "", caller_type: "", has_visual: false });
                        }
                      }}><Plus className="w-4 h-4" /></Button>
                    </div>
                    {newIncident.callback_numbers?.length > 0 && (
                      <div className="space-y-1 pl-2 border-l-2 border-slate-700">
                        {newIncident.callback_numbers.map((cb, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm bg-slate-800 px-2 py-1 rounded flex-wrap">
                            <Input
                              value={cb.number}
                              onChange={(e) => {
                                const updated = [...newIncident.callback_numbers];
                                updated[idx] = { ...updated[idx], number: e.target.value };
                                setNewIncident({ ...newIncident, callback_numbers: updated });
                              }}
                              className="input-dark h-7 w-32 text-sm"
                              type="tel"
                              placeholder="Phone..."
                            />
                            <Select
                              value={cb.caller_type || "_none"}
                              onValueChange={(v) => {
                                const updated = [...newIncident.callback_numbers];
                                updated[idx] = { ...updated[idx], caller_type: v === "_none" ? "" : v };
                                setNewIncident({ ...newIncident, callback_numbers: updated });
                              }}
                            >
                              <SelectTrigger className="input-dark w-[70px] h-7"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_none">-</SelectItem>
                                <SelectItem value="cv">CV</SelectItem>
                                <SelectItem value="3rd_party">3rd</SelectItem>
                              </SelectContent>
                            </Select>
                            <div className="flex items-center gap-1">
                              <Checkbox
                                checked={cb.has_visual || false}
                                onCheckedChange={(checked) => {
                                  const updated = [...newIncident.callback_numbers];
                                  updated[idx] = { ...updated[idx], has_visual: !!checked };
                                  setNewIncident({ ...newIncident, callback_numbers: updated });
                                }}
                              />
                              <span className="text-xs text-slate-400">Vis</span>
                            </div>
                            <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400 hover:text-blue-400" title="Lookup" onClick={() => setPhoneLookupNumber(cb.number)}><Search className="w-3 h-3" /></Button>
                            <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => setDeleteCallbackIndex(idx)}><X className="w-3 h-3" /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Perpetrators Section */}
                  <div className="p-3 bg-amber-950/30 border border-amber-800/50 rounded-sm space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs text-amber-400 uppercase font-medium flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Perpetrators ({newIncident.perp_descriptions?.length || 0})</p>
                      <Button type="button" size="sm" className="h-7 px-3 text-xs bg-amber-600 hover:bg-amber-700 text-white" onClick={() => { resetNewPerp(); setEditingPerpIndex(null); setShowPerpDialog(true); }}><Plus className="w-3 h-3 mr-1" />Add</Button>
                    </div>
                    {newIncident.perp_descriptions?.length > 0 && (
                      <div className="space-y-2">
                        {newIncident.perp_descriptions.map((p, idx) => (
                          <div key={idx} className="p-2 bg-slate-800 rounded border border-slate-700">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                {p.has_weapon && <Badge className="bg-red-600 text-white text-[10px]">ARMED</Badge>}
                                {p.was_arrested && <Badge className="bg-green-600 text-white text-[10px]">ARRESTED</Badge>}
                                {p.linked_profile_id && <Badge className="bg-purple-600 text-white text-[10px]">LINKED: {p.linked_profile_name || p.linked_profile_id}</Badge>}
                              </div>
                              <div className="flex gap-1">
                                <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => {
                                  setNewPerp({ ...p });
                                  setEditingPerpIndex(idx);
                                  setShowPerpDialog(true);
                                }}><Edit className="w-3 h-3" /></Button>
                                <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => {
                                  const updatedPerps = newIncident.perp_descriptions.filter((_, i) => i !== idx);
                                  const weaponSync = syncWeaponFromPerps(updatedPerps);
                                  setNewIncident({ ...newIncident, perp_descriptions: updatedPerps, ...weaponSync });
                                }}><X className="w-3 h-3" /></Button>
                              </div>
                            </div>
                            <div className="text-xs text-slate-300 space-y-0.5">
                              {p.name && <p><span className="text-slate-500">Name:</span> {p.name}</p>}
                              {(p.sex || p.race || p.age) && <p>{[p.sex, p.race, p.age && `~${p.age}`].filter(Boolean).join(", ")}</p>}
                              {(p.height || p.weight || p.build) && <p>{[p.height, p.weight, p.build].filter(Boolean).join(", ")}</p>}
                              {(p.hair_color_cut || p.eye_color || p.complexion) && <p>{[p.hair_color_cut && `Hair: ${p.hair_color_cut}`, p.eye_color && `Eyes: ${p.eye_color}`, p.complexion].filter(Boolean).join(", ")}</p>}
                              {(p.hat_color_style || p.shirt || p.pants || p.jacket_coat || p.shoes || p.clothing) && <p className="text-slate-400">{[p.hat_color_style && `Hat: ${p.hat_color_style}`, p.shirt && `Shirt: ${p.shirt}`, p.jacket_coat && `Jacket: ${p.jacket_coat}`, p.pants && `Pants: ${p.pants}`, p.shoes && `Shoes: ${p.shoes}`, p.clothing].filter(Boolean).join(" | ")}</p>}
                              {p.visible_marks && <p><span className="text-slate-500">Marks:</span> {p.visible_marks}</p>}
                              {p.direction_of_flight && <p className="text-amber-400"><span className="text-slate-500">DOF:</span> {p.direction_of_flight}</p>}
                              {p.current_location && <p className="text-amber-400"><span className="text-slate-500">Location:</span> {p.current_location}</p>}
                              {p.vehicle_description && <p><span className="text-slate-500">Vehicle:</span> {p.vehicle_description} {p.license_plate && `(${p.license_plate})`}</p>}
                              {p.weapon_description && <p className="text-red-400"><span className="text-slate-500">Weapon:</span> {p.weapon_description}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Dispatcher, Hotline & Notes */}
                  <div className="p-3 bg-slate-800 rounded-sm space-y-3">
                    <div className="grid grid-cols-2 gap-2 items-end">
                      <div>
                        <Label className="text-xs">Dispatcher</Label>
                        <Select value={newIncident.dispatch_id || "none"} onValueChange={(v) => setNewIncident({ ...newIncident, dispatch_id: v === "none" ? "" : v })}>
                          <SelectTrigger className="input-dark h-8 text-xs"><SelectValue placeholder="Select dispatcher..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {dispatchers.map((d) => <SelectItem key={d.unit_id} value={d.name}>{d.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-2 h-8">
                        <Checkbox id="new_hotline" checked={newIncident.is_hotline_call} onCheckedChange={(checked) => setNewIncident({ ...newIncident, is_hotline_call: checked })} />
                        <Label htmlFor="new_hotline" className={newIncident.is_hotline_call ? "text-amber-400 font-bold" : ""}>Hotline Call</Label>
                      </div>
                    </div>
                    {/* Job Narrative */}
                    <div>
                      <Label className="text-xs">Job Narrative / Notes</Label>
                      <Textarea
                        value={newIncident.job_body || ""}
                        onChange={(e) => setNewIncident({ ...newIncident, job_body: e.target.value })}
                        placeholder="Enter job details, description, notes..."
                        className="input-dark min-h-[80px]"
                      />
                    </div>
                  </div>

                  {/* Report Type, Status, Confirmation */}
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs">Report Type</Label>
                      <Select value={newIncident.report_type} onValueChange={(v) => setNewIncident({ ...newIncident, report_type: v })}>
                        <SelectTrigger className="input-dark h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="reported_live">Live</SelectItem>
                          <SelectItem value="reported_after">Past</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Status</Label>
                      <Select value={newIncident.status} onValueChange={(v) => setNewIncident({ ...newIncident, status: v })}>
                        <SelectTrigger className="input-dark h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ").toUpperCase()}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Confirmation</Label>
                      <Select value={newIncident.confirmation_status || "no_confirm"} onValueChange={(v) => setNewIncident({ ...newIncident, confirmation_status: v === "no_confirm" ? "" : v })}>
                        <SelectTrigger className="input-dark h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="no_confirm">No Confirm</SelectItem>
                          <SelectItem value="unconfirmed">Unconfirmed</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="unfounded">Unfounded</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Additional Details */}
                  <Button variant="outline" className="w-full text-sm border-slate-600 text-slate-400 hover:text-slate-200 hover:bg-slate-800" onClick={() => setShowExtraDetails(true)}>
                    <FileText className="w-4 h-4 mr-2" />Additional Details
                  </Button>

                  {/* Send WhatsApp to TARU */}
                  <div className="flex items-center gap-2 px-1">
                    <Checkbox
                      id="send-whatsapp-to-taru"
                      checked={newIncident.send_whatsapp_to_taru !== false}
                      onCheckedChange={(checked) => setNewIncident({ ...newIncident, send_whatsapp_to_taru: !!checked })}
                    />
                    <Label htmlFor="send-whatsapp-to-taru" className="text-sm cursor-pointer">
                      Send WhatsApp to TARU
                    </Label>
                  </div>

                  {/* Victims Section */}
                  <div className="p-3 bg-slate-800 rounded-sm space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs text-slate-500 uppercase">Victims ({newIncident.victims?.length || 0})</p>
                      <Button type="button" variant="outline" size="sm" className="h-6 px-2 text-xs" onClick={() => setNewVictim({ name: "", contact: "", showInput: true })}><Plus className="w-3 h-3 mr-1" />Add</Button>
                    </div>
                    {newIncident.victims?.length > 0 && (
                      <div className="space-y-1">
                        {newIncident.victims.map((v, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm bg-slate-700 px-2 py-1 rounded">
                            <span className="font-medium">{v.name || 'Victim ' + (idx + 1)}</span>
                            {v.contact && <span className="text-slate-400">| {v.contact}</span>}
                            <Button type="button" variant="ghost" size="sm" className="ml-auto h-6 w-6 p-0 text-red-400" onClick={() => setNewIncident({ ...newIncident, victims: newIncident.victims.filter((_, i) => i !== idx) })}><X className="w-3 h-3" /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                    {newVictim.showInput && (
                      <div className="flex gap-2 border-t border-slate-700 pt-2">
                        <Input value={newVictim.name} onChange={(e) => setNewVictim({ ...newVictim, name: e.target.value })} placeholder="Name" className="input-dark flex-1" autoFocus />
                        <Input value={newVictim.contact} onChange={(e) => setNewVictim({ ...newVictim, contact: e.target.value })} placeholder="Contact" className="input-dark flex-1" />
                        <Button type="button" variant="outline" size="sm" onClick={() => {
                          if (newVictim.name.trim() || newVictim.contact.trim()) { setNewIncident({ ...newIncident, victims: [...(newIncident.victims || []), { victim_id: `vic_${Date.now()}`, name: newVictim.name, contact: newVictim.contact }] }); }
                          setNewVictim({ name: "", contact: "", showInput: false });
                        }}>Add</Button>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setNewVictim({ name: "", contact: "", showInput: false })}><X className="w-4 h-4" /></Button>
                      </div>
                    )}
                  </div>

                  {/* Witnesses Section */}
                  <div className="p-3 bg-slate-800 rounded-sm space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs text-slate-500 uppercase">Witnesses ({newIncident.witnesses?.length || 0})</p>
                      <Button type="button" variant="outline" size="sm" className="h-6 px-2 text-xs" onClick={() => setNewWitness({ name: "", contact: "", showInput: true })}><Plus className="w-3 h-3 mr-1" />Add</Button>
                    </div>
                    {newIncident.witnesses?.length > 0 && (
                      <div className="space-y-1">
                        {newIncident.witnesses.map((w, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm bg-slate-700 px-2 py-1 rounded">
                            <span className="font-medium">{w.name || 'Witness ' + (idx + 1)}</span>
                            {w.contact && <span className="text-slate-400">| {w.contact}</span>}
                            <Button type="button" variant="ghost" size="sm" className="ml-auto h-6 w-6 p-0 text-red-400" onClick={() => setNewIncident({ ...newIncident, witnesses: newIncident.witnesses.filter((_, i) => i !== idx) })}><X className="w-3 h-3" /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                    {newWitness.showInput && (
                      <div className="flex gap-2 border-t border-slate-700 pt-2">
                        <Input value={newWitness.name} onChange={(e) => setNewWitness({ ...newWitness, name: e.target.value })} placeholder="Name" className="input-dark flex-1" autoFocus />
                        <Input value={newWitness.contact} onChange={(e) => setNewWitness({ ...newWitness, contact: e.target.value })} placeholder="Contact" className="input-dark flex-1" />
                        <Button type="button" variant="outline" size="sm" onClick={() => {
                          if (newWitness.name.trim() || newWitness.contact.trim()) { setNewIncident({ ...newIncident, witnesses: [...(newIncident.witnesses || []), { witness_id: `wit_${Date.now()}`, name: newWitness.name, contact: newWitness.contact }] }); }
                          setNewWitness({ name: "", contact: "", showInput: false });
                        }}>Add</Button>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setNewWitness({ name: "", contact: "", showInput: false })}><X className="w-4 h-4" /></Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* Floating Footer */}
              <div className="flex-shrink-0 border-t border-slate-700 bg-slate-900 px-6 py-4 space-y-3">
                <Button onClick={() => handleCreateIncident()} className="w-full btn-primary h-12 text-base font-semibold" data-testid="create-incident-btn">Create Incident</Button>
                <Button variant="outline" className="w-full text-sm border-blue-600 text-blue-400 hover:bg-blue-950" onClick={() => setShowLiveCallOptions(!showLiveCallOptions)}>
                  <Phone className="w-4 h-4 mr-2" />Live Call Options {showLiveCallOptions ? '▲' : '▼'}
                </Button>
                {showLiveCallOptions && (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Button variant="outline" className="border-green-600 text-green-400 hover:bg-green-950 text-xs" onClick={async () => {
                      if (!newIncident.incident_type) { toast.error("Please select an incident type"); return; }
                      try {
                        const allCallbacks = [...(newIncident.callback_numbers || [])];
                        if (newCallbackNumber.number?.trim()) allCallbacks.push({ number: newCallbackNumber.number.trim(), caller_type: newCallbackNumber.caller_type, has_visual: newCallbackNumber.has_visual });
                        const liveCallData = {
                          priority: newIncident.priority === "critical" || newIncident.priority === "high" ? "priority" : "normal",
                          location: newIncident.address || "",
                          location_notes: newIncident.location_notes || "",
                          cross_streets: newIncident.cross_streets || "",
                          call_type: newIncident.incident_type,
                          secondary_call_type: newIncident.secondary_incident_type || "",
                          call_note: newIncident.job_body || "",
                          callback_numbers: allCallbacks,
                          cv_or_third_party: newIncident.cv_or_third_party || "",
                          caller_has_visual: newIncident.caller_has_visual,
                          direction_of_flight: newIncident.direction_of_flight || "",
                          confirmation_status: newIncident.confirmation_status,
                          transmitted_10_85: newIncident.transmitted_10_85,
                          transmitted_10_13: newIncident.transmitted_10_13,
                          pd_activated: newIncident.pd_activated,
                          hems_activated: newIncident.ems_required,
                          is_hotline_call: newIncident.is_hotline_call,
                          dispatcher_units: newIncident.dispatch_id ? [newIncident.dispatch_id] : [],
                          victims: newIncident.victims || [],
                          witnesses: newIncident.witnesses || [],
                          perp_descriptions: newIncident.perp_descriptions || [],
                          call_mode: "live_call"
                        };
                        await axios.post(`${API}/live-calls`, liveCallData, { withCredentials: true });
                        setShowNewDialog(false);
                        setShowLiveCallOptions(false);
                        resetNewIncidentForm();
                        toast.success("Live call created");
                        navigate("/live-calls");
                      } catch (error) { toast.error("Failed to create live call"); }
                    }}>Create Live Call</Button>
                    <Button variant="outline" className="border-blue-600 text-blue-400 hover:bg-blue-950 text-xs" onClick={async () => {
                      if (!newIncident.incident_type) { toast.error("Please select an incident type"); return; }
                      try {
                        const allCallbacks = [...(newIncident.callback_numbers || [])];
                        if (newCallbackNumber.number?.trim()) allCallbacks.push({ number: newCallbackNumber.number.trim(), caller_type: newCallbackNumber.caller_type, has_visual: newCallbackNumber.has_visual });
                        const liveCallData = {
                          priority: newIncident.priority === "critical" || newIncident.priority === "high" ? "priority" : "normal",
                          location: newIncident.address || "",
                          location_notes: newIncident.location_notes || "",
                          cross_streets: newIncident.cross_streets || "",
                          call_type: newIncident.incident_type,
                          secondary_call_type: newIncident.secondary_incident_type || "",
                          call_note: newIncident.job_body || "",
                          callback_numbers: allCallbacks,
                          cv_or_third_party: newIncident.cv_or_third_party || "",
                          caller_has_visual: newIncident.caller_has_visual,
                          direction_of_flight: newIncident.direction_of_flight || "",
                          confirmation_status: newIncident.confirmation_status,
                          transmitted_10_85: newIncident.transmitted_10_85,
                          transmitted_10_13: newIncident.transmitted_10_13,
                          pd_activated: newIncident.pd_activated,
                          hems_activated: newIncident.ems_required,
                          is_hotline_call: newIncident.is_hotline_call,
                          dispatcher_units: newIncident.dispatch_id ? [newIncident.dispatch_id] : [],
                          victims: newIncident.victims || [],
                          witnesses: newIncident.witnesses || [],
                          perp_descriptions: newIncident.perp_descriptions || [],
                          call_mode: "live_call"
                        };
                        await axios.post(`${API}/live-calls`, liveCallData, { withCredentials: true });
                        resetNewIncidentForm();
                        toast.success("Live call created - form reset for next entry");
                      } catch (error) { toast.error("Failed to create live call"); }
                    }}>Submit &amp; Stay Open</Button>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Short form — date/time, dispatcher, address, types, notes, units,
              perps, hate crime. Shares newIncident state with the full form. */}
          <Dialog open={showQuickDialog} onOpenChange={(open) => {
              if (!open) {
                if (showPerpDialog) return;
                resetNewIncidentForm();
              }
              setShowQuickDialog(open);
            }}>
            <DialogContent
              className={`w-[calc(100vw-2rem)] max-w-lg bg-slate-900 border-slate-800 max-h-[90vh] overflow-hidden flex flex-col p-0 ${showPerpDialog ? "opacity-0 pointer-events-none" : ""}`}
              onPointerDownOutside={(e) => { if (showPerpDialog) e.preventDefault(); }}
              onInteractOutside={(e) => { if (showPerpDialog) e.preventDefault(); }}
              onEscapeKeyDown={(e) => { if (showPerpDialog) e.preventDefault(); }}
            >
              <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
                <DialogTitle className="uppercase tracking-wider">New Incident — Short Form</DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto overflow-x-hidden px-6">
                <div className="space-y-4 mt-4 pb-4">
                  {/* Date and Time */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={newIncident.date || new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' })}
                        onChange={(e) => setNewIncident({ ...newIncident, date: e.target.value })}
                        className="input-dark"
                      />
                    </div>
                    <div>
                      <Label>Time</Label>
                      <Input
                        type="time"
                        value={newIncident.time || new Date().toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false })}
                        onChange={(e) => setNewIncident({ ...newIncident, time: e.target.value })}
                        className="input-dark"
                      />
                    </div>
                  </div>

                  {/* Dispatcher */}
                  <div>
                    <Label>Dispatcher ID</Label>
                    <Select value={newIncident.dispatch_id || "none"} onValueChange={(v) => setNewIncident({ ...newIncident, dispatch_id: v === "none" ? "" : v })}>
                      <SelectTrigger className="input-dark" data-testid="quick-dispatcher-select"><SelectValue placeholder="Select dispatcher..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {dispatchers.map((d) => <SelectItem key={d.unit_id} value={d.name}>{d.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Address */}
                  <div>
                    <Label>Address *</Label>
                    <div className="relative">
                      <Input
                        value={newIncident.address || ""}
                        onChange={(e) => handleAddressChange(e.target.value)}
                        onFocus={() => { if (enableLocationPredictions && locationPredictions.length > 0) setShowLocationPredictions(true); }}
                        onBlur={() => { setTimeout(() => setShowLocationPredictions(false), 200); handleAddressBlur(); }}
                        placeholder="Enter location..."
                        className="input-dark"
                        autoComplete="off"
                        data-testid="quick-location-input"
                      />
                      {showLocationPredictions && locationPredictions.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-md shadow-lg max-h-48 overflow-y-auto">
                          {locationPredictions.map((prediction, idx) => (
                            <div
                              key={prediction.place_id || idx}
                              className="px-3 py-2 cursor-pointer text-sm hover:bg-slate-700 border-b border-slate-700/50 last:border-0"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => selectLocationPrediction(prediction)}
                            >
                              <div className="font-medium text-slate-200">{prediction.main_text}</div>
                              {prediction.secondary_text && <div className="text-xs text-slate-400">{prediction.secondary_text}</div>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Call Type */}
                  <div>
                    <Label>Call Type *</Label>
                    {renderQuickTypeInput({
                      field: "incident_type",
                      search: callTypeSearch,
                      setSearch: setCallTypeSearch,
                      open: showCallTypeDropdown,
                      setOpen: setShowCallTypeDropdown,
                      highlighted: callTypeHighlightedIndex,
                      setHighlighted: setCallTypeHighlightedIndex,
                      placeholder: "Search or enter type...",
                    })}
                  </div>

                  {/* Secondary Type */}
                  <div>
                    <Label>Secondary Type (Optional)</Label>
                    {renderQuickTypeInput({
                      field: "secondary_incident_type",
                      search: secondaryCallTypeSearch,
                      setSearch: setSecondaryCallTypeSearch,
                      open: showSecondaryCallTypeDropdown,
                      setOpen: setShowSecondaryCallTypeDropdown,
                      highlighted: secondaryCallTypeHighlightedIndex,
                      setHighlighted: setSecondaryCallTypeHighlightedIndex,
                      placeholder: "Search or enter secondary type...",
                    })}
                  </div>

                  {/* Callback Numbers — number, CV/3rd and add. A number left
                      in the input is still saved on create. */}
                  <div className="space-y-2">
                    <Label>Callback Numbers</Label>
                    <div className="flex gap-2 items-center">
                      <Input
                        value={newCallbackNumber.number}
                        onChange={(e) => setNewCallbackNumber({ ...newCallbackNumber, number: formatPhoneNumber(e.target.value) })}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && newCallbackNumber.number.trim()) {
                            e.preventDefault();
                            setNewIncident({ ...newIncident, callback_numbers: [...(newIncident.callback_numbers || []), { number: newCallbackNumber.number, caller_type: newCallbackNumber.caller_type, has_visual: newCallbackNumber.has_visual }] });
                            setNewCallbackNumber({ number: "", caller_type: "", has_visual: false });
                          }
                        }}
                        placeholder="Phone number..."
                        className="input-dark flex-1 min-w-0"
                        type="tel"
                        inputMode="numeric"
                        data-testid="quick-callback-input"
                      />
                      <Button type="button" variant="outline" size="sm" title="Paste from clipboard" className="h-8 px-2 shrink-0" onClick={async () => {
                        try { const text = await navigator.clipboard.readText(); if (text) setNewCallbackNumber({ ...newCallbackNumber, number: formatPhoneNumber(text.trim()) }); }
                        catch (e) { toast.error("Could not access clipboard"); }
                      }}><ClipboardPaste className="w-4 h-4" /></Button>
                      <Select value={newCallbackNumber.caller_type || ""} onValueChange={(v) => setNewCallbackNumber({ ...newCallbackNumber, caller_type: v })}>
                        <SelectTrigger className="input-dark w-[70px] shrink-0"><SelectValue placeholder="Type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cv">CV</SelectItem>
                          <SelectItem value="3rd_party">3rd</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button type="button" variant="outline" size="sm" className="shrink-0" onClick={() => {
                        if (newCallbackNumber.number.trim()) {
                          setNewIncident({ ...newIncident, callback_numbers: [...(newIncident.callback_numbers || []), { number: newCallbackNumber.number, caller_type: newCallbackNumber.caller_type, has_visual: newCallbackNumber.has_visual }] });
                          setNewCallbackNumber({ number: "", caller_type: "", has_visual: false });
                        }
                      }}><Plus className="w-4 h-4" /></Button>
                    </div>
                    {newIncident.callback_numbers?.length > 0 && (
                      <div className="space-y-1 pl-2 border-l-2 border-slate-700">
                        {newIncident.callback_numbers.map((cb, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm bg-slate-800 px-2 py-1 rounded">
                            <Phone className="w-3 h-3 text-slate-400 shrink-0" />
                            <span className="font-mono truncate">{cb.number}</span>
                            {cb.caller_type && <Badge variant="secondary" className="text-[10px] shrink-0">{cb.caller_type === "cv" ? "CV" : "3RD"}</Badge>}
                            <Button type="button" variant="ghost" size="sm" className="ml-auto h-6 w-6 p-0 text-red-400 shrink-0" onClick={() => setNewIncident({ ...newIncident, callback_numbers: newIncident.callback_numbers.filter((_, i) => i !== idx) })}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Notes */}
                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      value={newIncident.job_body || ""}
                      onChange={(e) => setNewIncident({ ...newIncident, job_body: e.target.value })}
                      placeholder="Enter job details, description, notes..."
                      className="input-dark min-h-[80px]"
                      data-testid="quick-notes-input"
                    />
                  </div>

                  {/* Units Assigned — same picker as the dispatch call detail:
                      badges for what's assigned, a search box that lists
                      matching units while you type. */}
                  <div>
                    <Label className="flex items-center gap-2"><Users className="w-4 h-4" />Units Assigned ({(newIncident.units_responded || []).length})</Label>
                    <div className="mt-1.5">
                      <div className="flex flex-wrap gap-2 mb-3">
                        {[...(newIncident.units_responded || [])].sort((a, b) => {
                          const numA = parseInt(a.replace(/\D/g, '')) || 0;
                          const numB = parseInt(b.replace(/\D/g, '')) || 0;
                          if (numA !== numB) return numA - numB;
                          return a.localeCompare(b);
                        }).map((unit) => (
                          <Badge
                            key={unit}
                            className="font-mono cursor-pointer transition-all select-none px-2.5 py-1 bg-slate-300 dark:bg-slate-600 text-slate-800 dark:text-slate-200 hover:bg-red-500 dark:hover:bg-red-600 hover:text-white"
                            onClick={() => setNewIncident({ ...newIncident, units_responded: (newIncident.units_responded || []).filter(u => u !== unit) })}
                            title="Click to unassign"
                          >
                            {unit}
                          </Badge>
                        ))}
                        {(newIncident.units_responded || []).length === 0 && (
                          <span className="text-xs text-slate-500">No units assigned</span>
                        )}
                      </div>
                      {(() => {
                        // Computed once so the key handler and the list agree.
                        const availableUnits = units.filter(u =>
                          u.name.toLowerCase().includes(quickUnitSearch.toLowerCase()) &&
                          !(newIncident.units_responded || []).includes(u.name)
                        );
                        const assign = (name) => {
                          setNewIncident({ ...newIncident, units_responded: [...(newIncident.units_responded || []), name] });
                          setQuickUnitSearch("");
                          setQuickUnitHighlight(0);
                        };
                        return (
                          <>
                            <div className="relative">
                              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                              <Input
                                value={quickUnitSearch}
                                onChange={(e) => { setQuickUnitSearch(e.target.value); setQuickUnitHighlight(0); }}
                                onKeyDown={(e) => {
                                  if (!quickUnitSearch || availableUnits.length === 0) return;
                                  if (e.key === "ArrowDown") {
                                    e.preventDefault();
                                    setQuickUnitHighlight(prev => (prev < availableUnits.length - 1 ? prev + 1 : 0));
                                  } else if (e.key === "ArrowUp") {
                                    e.preventDefault();
                                    setQuickUnitHighlight(prev => (prev > 0 ? prev - 1 : availableUnits.length - 1));
                                  } else if (e.key === "Enter") {
                                    e.preventDefault();
                                    const idx = quickUnitHighlight >= 0 && quickUnitHighlight < availableUnits.length ? quickUnitHighlight : 0;
                                    assign(availableUnits[idx].name);
                                  } else if (e.key === "Escape") {
                                    e.preventDefault();
                                    setQuickUnitSearch("");
                                    setQuickUnitHighlight(0);
                                  }
                                }}
                                placeholder="Search units to assign..."
                                className="input-dark pl-8 h-8 text-sm"
                                inputMode="numeric"
                                autoComplete="off"
                                data-testid="quick-add-unit"
                              />
                            </div>
                            {quickUnitSearch && (
                              <div className="mt-2">
                                <ScrollArea className="max-h-40">
                                  <div className="space-y-1">
                                    {availableUnits.map((unit, idx) => (
                                      <div
                                        key={unit.unit_id}
                                        className={`p-2 text-sm rounded flex items-center gap-2 cursor-pointer ${idx === quickUnitHighlight ? "bg-blue-900/40 ring-1 ring-blue-500" : "hover:bg-slate-800"}`}
                                        onMouseEnter={() => setQuickUnitHighlight(idx)}
                                        onClick={() => assign(unit.name)}
                                      >
                                        <Plus className="w-4 h-4 text-blue-400" />
                                        <span className="flex-1">{unit.name}</span>
                                      </div>
                                    ))}
                                  </div>
                                </ScrollArea>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Perpetrators */}
                  <div className="p-3 bg-amber-950/30 border border-amber-800/50 rounded-sm space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs text-amber-400 uppercase font-medium flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Perpetrators ({newIncident.perp_descriptions?.length || 0})</p>
                      <Button type="button" size="sm" className="h-7 px-3 text-xs bg-amber-600 hover:bg-amber-700 text-white" onClick={() => { resetNewPerp(); setEditingPerpIndex(null); setShowPerpDialog(true); }}><Plus className="w-3 h-3 mr-1" />Add</Button>
                    </div>
                    {newIncident.perp_descriptions?.length > 0 && (
                      <div className="space-y-2">
                        {newIncident.perp_descriptions.map((p, idx) => (
                          <div key={idx} className="p-2 bg-slate-800 rounded border border-slate-700">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1 text-xs text-slate-300 space-y-0.5">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {p.has_weapon && <Badge className="bg-red-600 text-white text-[10px]">ARMED</Badge>}
                                  {p.was_arrested && <Badge className="bg-green-600 text-white text-[10px]">ARRESTED</Badge>}
                                </div>
                                {p.name && <p><span className="text-slate-500">Name:</span> {p.name}</p>}
                                {(p.sex || p.race || p.age) && <p>{[p.sex, p.race, p.age && `~${p.age}`].filter(Boolean).join(", ")}</p>}
                                {(p.height || p.weight || p.build) && <p>{[p.height, p.weight, p.build].filter(Boolean).join(", ")}</p>}
                                {(p.hat_color_style || p.shirt || p.pants || p.jacket_coat || p.shoes || p.clothing) && <p className="text-slate-400">{[p.hat_color_style && `Hat: ${p.hat_color_style}`, p.shirt && `Shirt: ${p.shirt}`, p.jacket_coat && `Jacket: ${p.jacket_coat}`, p.pants && `Pants: ${p.pants}`, p.shoes && `Shoes: ${p.shoes}`, p.clothing].filter(Boolean).join(" | ")}</p>}
                                {p.weapon_description && <p className="text-red-400"><span className="text-slate-500">Weapon:</span> {p.weapon_description}</p>}
                              </div>
                              <div className="flex gap-1 shrink-0">
                                <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => {
                                  setNewPerp({ ...p });
                                  setEditingPerpIndex(idx);
                                  setShowPerpDialog(true);
                                }}><Edit className="w-3 h-3" /></Button>
                                <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => {
                                  const updatedPerps = newIncident.perp_descriptions.filter((_, i) => i !== idx);
                                  const weaponSync = syncWeaponFromPerps(updatedPerps);
                                  setNewIncident({ ...newIncident, perp_descriptions: updatedPerps, ...weaponSync });
                                }}><X className="w-3 h-3" /></Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Hate Crime */}
                  <div className="flex items-center gap-2">
                    <Checkbox id="quick_hate_crime" checked={newIncident.is_hate_crime || false} onCheckedChange={(checked) => setNewIncident({ ...newIncident, is_hate_crime: checked })} />
                    <Label htmlFor="quick_hate_crime" className={newIncident.is_hate_crime ? "text-red-400 font-bold" : ""}>Hate Crime</Label>
                  </div>
                </div>
              </div>
              {/* Floating Footer */}
              <div className="flex-shrink-0 border-t border-slate-700 bg-slate-900 px-6 py-4 grid grid-cols-2 gap-2">
                <Button onClick={() => handleCreateIncident()} className="btn-primary h-12 text-base font-semibold" data-testid="quick-create-btn">Create</Button>
                <Button onClick={() => handleCreateIncident(true)} variant="outline" className="h-12 text-base font-semibold border-blue-600 text-blue-400 hover:bg-blue-950" data-testid="quick-create-new-btn">Create &amp; New</Button>
              </div>
            </DialogContent>
          </Dialog>

          {renderPaginationCompact()}
        </div>
      </div>

      {/* Search and Filters */}
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-4">
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder={searchField === "all" ? "Search all fields..." : `Search in ${searchField}...`}
              className="pl-10 input-dark h-10"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Select value={searchField} onValueChange={setSearchField}>
              <SelectTrigger className="w-28 sm:w-36 h-9 bg-slate-800 border-slate-700 text-sm shrink-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Fields</SelectItem>
                <SelectItem value="headline">Headline</SelectItem>
                <SelectItem value="id">Incident ID</SelectItem>
                <SelectItem value="address">Address</SelectItem>
                <SelectItem value="callback">Callbacks</SelectItem>
                <SelectItem value="victim">Victims</SelectItem>
                <SelectItem value="witness">Witnesses</SelectItem>
                <SelectItem value="perp">Perps</SelectItem>
                <SelectItem value="description">Description</SelectItem>
                <SelectItem value="notes">Notes</SelectItem>
                <SelectItem value="unit">Units</SelectItem>
                <SelectItem value="dispatcher">Dispatcher</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="shrink-0" onClick={() => setShowFilters(!showFilters)} data-testid="filter-toggle">
              <Filter className="w-4 h-4" />
            </Button>
            <Button size="sm" onClick={handleSearch} className="btn-primary shrink-0" data-testid="search-btn">
              Search
            </Button>
            <div className="ml-auto hidden md:flex items-center gap-1 bg-slate-800 rounded p-0.5">
              <button
                onClick={() => { setViewMode("cards"); localStorage.setItem("incidents_view", "cards"); }}
                className={`p-1.5 rounded transition-colors ${viewMode === "cards" ? "bg-slate-600 text-white" : "text-slate-400 hover:text-white"}`}
                title="Card view"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => { setViewMode("list"); localStorage.setItem("incidents_view", "list"); }}
                className={`p-1.5 rounded transition-colors ${viewMode === "list" ? "bg-slate-600 text-white" : "text-slate-400 hover:text-white"}`}
                title="List view"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => { setViewMode("map"); localStorage.setItem("incidents_view", "map"); }}
                className={`p-1.5 rounded transition-colors ${viewMode === "map" ? "bg-slate-600 text-white" : "text-slate-400 hover:text-white"}`}
                title="Map view"
              >
                <MapPinned className="w-4 h-4" />
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-2 mb-4">
                <Button
                  variant="outline"
                  size="sm"
                  className={`shrink-0 ${showPhoneNumbers ? "bg-amber-600/20 border-amber-500 text-amber-400" : ""}`}
                  onClick={() => setShowPhoneNumbers(!showPhoneNumbers)}
                >
                  <Phone className="w-4 h-4 mr-1.5" />
                  Phone Numbers
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={`shrink-0 ${showCreatedDate ? "bg-blue-600/20 border-blue-500 text-blue-400" : ""}`}
                  onClick={() => setShowCreatedDate(!showCreatedDate)}
                  title="Show the date/time the incident record was created (separate from the incident's reported date/time)"
                >
                  <Clock className="w-4 h-4 mr-1.5" />
                  Created Date
                </Button>
              </div>
              {/* Filter Sections in Cards */}
              <div className="space-y-4">

                {/* Primary Filters */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="bg-slate-100 dark:bg-slate-800/50 rounded-lg p-3">
                    <Label className="text-xs text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-2 block">Type</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full h-9 justify-between text-sm bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 font-normal">
                          {filterType || "All Types"}
                          <ChevronRight className="w-3 h-3 rotate-90 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56 p-0 bg-slate-900 border-slate-700" align="start">
                        <div className="p-2 border-b border-slate-700">
                          <Input
                            placeholder="Search types..."
                            className="h-7 text-sm bg-slate-800 border-slate-700"
                            onChange={(e) => {
                              const el = e.target.closest('[data-radix-popper-content-wrapper]')?.querySelector('[data-type-list]');
                              if (el) el.dataset.search = e.target.value.toLowerCase();
                              // Force re-render of items
                              el?.querySelectorAll('[data-type-item]').forEach(item => {
                                item.style.display = item.dataset.typeName.toLowerCase().includes(e.target.value.toLowerCase()) ? '' : 'none';
                              });
                            }}
                            autoFocus
                          />
                        </div>
                        <div data-type-list className="max-h-60 overflow-y-auto p-1">
                          <button
                            data-type-item
                            data-type-name="all types"
                            className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-slate-800 ${!filterType ? "text-blue-400 font-medium" : "text-slate-300"}`}
                            onClick={() => { setFilterType(""); }}
                          >All Types</button>
                          {[...incidentTypes].sort((a, b) => a.name.localeCompare(b.name)).map((type) => (
                            <button
                              key={type.type_id}
                              data-type-item
                              data-type-name={type.name}
                              className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-slate-800 ${filterType === type.name ? "text-blue-400 font-medium" : "text-slate-300"}`}
                              onClick={() => { setFilterType(type.name); }}
                            >{type.name}</button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  <div className="bg-slate-100 dark:bg-slate-800/50 rounded-lg p-3">
                    <Label className="text-xs text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-2 block">Status</Label>
                    <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                      <SelectTrigger className="h-9 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700">
                        <SelectValue placeholder="All statuses" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        {STATUS_OPTIONS.map((s) => (
                          <SelectItem key={s} value={s}>{s.replace("_", " ").toUpperCase()}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="bg-slate-100 dark:bg-slate-800/50 rounded-lg p-3">
                    <Label className="text-xs text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-2 block">Confirmation</Label>
                    <Select value={filterConfirmation || "all"} onValueChange={(v) => setFilterConfirmation(v === "all" ? "" : v)}>
                      <SelectTrigger className="h-9 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="unconfirmed">Unconfirmed</SelectItem>
                        <SelectItem value="unfounded">Unfounded</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="bg-slate-100 dark:bg-slate-800/50 rounded-lg p-3">
                    <Label className="text-xs text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-2 block">Date Range</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="w-full h-9 justify-start text-left font-normal bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800">
                          <CalendarIcon className="mr-2 h-4 w-4 text-slate-500 dark:text-slate-400" />
                          {dateRange.from ? (
                            dateRange.to ? (
                              <span>{format(dateRange.from, "MMM d")} - {format(dateRange.to, "MMM d")}</span>
                            ) : format(dateRange.from, "MMM d, yyyy")
                          ) : <span className="text-slate-500 dark:text-slate-400">Pick dates</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-800" align="start">
                        <Calendar mode="range" selected={dateRange} onSelect={setDateRange} numberOfMonths={window.innerWidth >= 768 ? 2 : 1} captionLayout="dropdown-buttons" fromYear={2020} toYear={new Date().getFullYear() + 1} />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                
                {/* Secondary Filters */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="bg-slate-100 dark:bg-slate-800/50 rounded-lg p-3">
                    <Label className="text-xs text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-2 block">Report Type</Label>
                    <Select value={filterReportType || "all"} onValueChange={(v) => setFilterReportType(v === "all" ? "" : v)}>
                      <SelectTrigger className="h-9 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Report Types</SelectItem>
                        <SelectItem value="reported_live">Reported Live</SelectItem>
                        <SelectItem value="reported_after">Reported After</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="bg-slate-100 dark:bg-slate-800/50 rounded-lg p-3">
                    <Label className="text-xs text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-2 block">Units Responded</Label>
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-1 min-h-[32px] p-1 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded">
                        {filterUnits.length === 0 ? (
                          <span className="text-xs text-slate-500 p-1">All Units</span>
                        ) : (
                          filterUnits.map(unit => (
                            <Badge key={unit} variant="secondary" className="text-xs flex items-center gap-1">
                              {unit}
                              <X 
                                className="w-3 h-3 cursor-pointer hover:text-red-400" 
                                onClick={() => setFilterUnits(filterUnits.filter(u => u !== unit))}
                              />
                            </Badge>
                          ))
                        )}
                      </div>
                      <Select 
                        value="" 
                        onValueChange={(v) => {
                          if (v && !filterUnits.includes(v)) {
                            setFilterUnits([...filterUnits, v]);
                          }
                        }}
                      >
                        <SelectTrigger className="h-8 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-xs">
                          <SelectValue placeholder="Add unit..." />
                        </SelectTrigger>
                        <SelectContent>
                          {units.filter(u => !filterUnits.includes(u.name)).map((unit) => (
                            <SelectItem key={unit.unit_id} value={unit.name}>{unit.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="bg-slate-100 dark:bg-slate-800/50 rounded-lg p-3">
                    <Label className="text-xs text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-2 block">Unit Investigating</Label>
                    <Select value={filterUnitInvestigating || "all"} onValueChange={(v) => setFilterUnitInvestigating(v === "all" ? "" : v)}>
                      <SelectTrigger className="h-9 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        {units.filter(u => u.is_investigator).map((unit) => (
                          <SelectItem key={unit.unit_id} value={unit.name}>{unit.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="bg-slate-100 dark:bg-slate-800/50 rounded-lg p-3">
                    <Label className="text-xs text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-2 block">Dispatcher</Label>
                    <Select value={filterDispatcher || "all"} onValueChange={(v) => setFilterDispatcher(v === "all" ? "" : v)}>
                      <SelectTrigger className="h-9 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700">
                        <SelectValue placeholder="All dispatchers" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Dispatchers</SelectItem>
                        {dispatchers.map((d) => (
                          <SelectItem key={d.unit_id} value={d.name}>{d.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Profile Filter */}
                <div className="bg-purple-100 dark:bg-purple-950/30 border border-purple-300 dark:border-purple-900/50 rounded-lg p-3">
                  <Label className="text-xs text-purple-700 dark:text-purple-400 uppercase tracking-wide mb-3 block flex items-center gap-2">
                    <UserCircle className="w-4 h-4" /> Profile Filters
                  </Label>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="w-48">
                      <Select value={filterSpecificProfile || "all"} onValueChange={(v) => setFilterSpecificProfile(v === "all" ? "" : v)}>
                        <SelectTrigger className="h-9 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700">
                          <SelectValue placeholder="Select Profile" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Profiles</SelectItem>
                          {profiles.map((p) => (
                            <SelectItem key={p.profile_id} value={p.profile_id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-2 bg-slate-200 dark:bg-slate-800/50 rounded-md">
                      <Checkbox id="filter-any-profile" checked={filterHasAnyProfile} onCheckedChange={(c) => setFilterHasAnyProfile(c)} />
                      <Label htmlFor="filter-any-profile" className="text-sm cursor-pointer">Has Any Profile</Label>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-2 bg-amber-100 dark:bg-amber-950/50 rounded-md border border-amber-300 dark:border-amber-900/50">
                      <Checkbox id="filter-ready" checked={filterReadyForArrest} onCheckedChange={(c) => setFilterReadyForArrest(c)} />
                      <Label htmlFor="filter-ready" className="text-sm text-amber-700 dark:text-amber-400 cursor-pointer">Ready for Arrest</Label>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-2 bg-emerald-100 dark:bg-emerald-950/50 rounded-md border border-emerald-300 dark:border-emerald-900/50">
                      <Checkbox id="filter-arrested" checked={filterProfileArrested} onCheckedChange={(c) => setFilterProfileArrested(c)} />
                      <Label htmlFor="filter-arrested" className="text-sm text-emerald-700 dark:text-emerald-400 cursor-pointer">Arrested</Label>
                    </div>
                  </div>
                </div>
                
                {/* Quick Filters - Grouped by Category */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* Critical Flags */}
                  <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/30 rounded-lg p-3">
                    <Label className="text-xs text-red-700 dark:text-red-400 uppercase tracking-wide mb-3 block">Critical Flags</Label>
                    <div className="flex flex-wrap gap-2">
                      <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterArrest ? 'bg-emerald-200 dark:bg-emerald-600/30 border border-emerald-400 dark:border-emerald-500' : 'bg-white dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-700/50 border border-transparent'}`}>
                        <Checkbox id="filter-arrest" checked={filterArrest} onCheckedChange={(c) => setFilterArrest(c)} className="hidden" />
                        <span className={`text-sm ${filterArrest ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-700 dark:text-slate-300'}`}>🚔 Arrests</span>
                      </label>
                      <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterArrestVia ? 'bg-emerald-200 dark:bg-emerald-700/40 border border-emerald-400 dark:border-emerald-600' : 'bg-white dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-700/50 border border-transparent'}`} title="Arrests made via another incident's ready-for-arrest record">
                        <Checkbox id="filter-arrest-via" checked={filterArrestVia} onCheckedChange={(c) => setFilterArrestVia(c)} className="hidden" />
                        <span className={`text-sm ${filterArrestVia ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-700 dark:text-slate-300'}`}>🚔 Arrest Via</span>
                      </label>
                      <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterHateCrime ? 'bg-red-200 dark:bg-red-600/30 border border-red-400 dark:border-red-500' : 'bg-white dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-700/50 border border-transparent'}`}>
                        <Checkbox id="filter-hate" checked={filterHateCrime} onCheckedChange={(c) => setFilterHateCrime(c)} className="hidden" />
                        <span className={`text-sm ${filterHateCrime ? 'text-red-700 dark:text-red-400' : 'text-slate-700 dark:text-slate-300'}`}>⚠️ Hate Crime</span>
                      </label>
                      <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterWeapon ? 'bg-orange-200 dark:bg-orange-600/30 border border-orange-400 dark:border-orange-500' : 'bg-white dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-700/50 border border-transparent'}`}>
                        <Checkbox id="filter-weapon" checked={filterWeapon} onCheckedChange={(c) => setFilterWeapon(c)} className="hidden" />
                        <span className={`text-sm ${filterWeapon ? 'text-orange-700 dark:text-orange-400' : 'text-slate-700 dark:text-slate-300'}`}>🔫 Weapon</span>
                      </label>
                      <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterInjuries ? 'bg-red-200 dark:bg-red-600/30 border border-red-400 dark:border-red-500' : 'bg-white dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-700/50 border border-transparent'}`}>
                        <Checkbox id="filter-injuries" checked={filterInjuries} onCheckedChange={(c) => setFilterInjuries(c)} className="hidden" />
                        <span className={`text-sm ${filterInjuries ? 'text-red-700 dark:text-red-400' : 'text-slate-700 dark:text-slate-300'}`}>🩹 Injuries</span>
                      </label>
                      <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterMinorOrElderly ? 'bg-amber-200 dark:bg-amber-600/30 border border-amber-400 dark:border-amber-500' : 'bg-white dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-700/50 border border-transparent'}`}>
                        <Checkbox id="filter-minor-elderly" checked={filterMinorOrElderly} onCheckedChange={(c) => setFilterMinorOrElderly(c)} className="hidden" />
                        <span className={`text-sm ${filterMinorOrElderly ? 'text-amber-700 dark:text-amber-400' : 'text-slate-700 dark:text-slate-300'}`}>👶 Minor/Elderly</span>
                      </label>
                    </div>
                  </div>
                  
                  {/* Status Flags */}
                  <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900/30 rounded-lg p-3">
                    <Label className="text-xs text-blue-700 dark:text-blue-400 uppercase tracking-wide mb-3 block">Status Flags</Label>
                    <div className="flex flex-wrap gap-2">
                      <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterPR ? 'bg-amber-200 dark:bg-amber-600/30 border border-amber-400 dark:border-amber-500' : 'bg-white dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-700/50 border border-transparent'}`}>
                        <Checkbox id="filter-pr" checked={filterPR} onCheckedChange={(c) => setFilterPR(c)} className="hidden" />
                        <span className={`text-sm ${filterPR ? 'text-amber-700 dark:text-amber-400' : 'text-slate-700 dark:text-slate-300'}`}>📰 PR</span>
                      </label>
                      <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterPDActivated ? 'bg-blue-200 dark:bg-blue-600/30 border border-blue-400 dark:border-blue-500' : 'bg-white dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-700/50 border border-transparent'}`}>
                        <Checkbox id="filter-pd" checked={filterPDActivated} onCheckedChange={(c) => setFilterPDActivated(c)} className="hidden" />
                        <span className={`text-sm ${filterPDActivated ? 'text-blue-700 dark:text-blue-400' : 'text-slate-700 dark:text-slate-300'}`}>🚨 PD Activated</span>
                      </label>
                      <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterNeedsFollowup ? 'bg-yellow-200 dark:bg-yellow-600/30 border border-yellow-400 dark:border-yellow-500' : 'bg-white dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-700/50 border border-transparent'}`}>
                        <Checkbox id="filter-followup" checked={filterNeedsFollowup} onCheckedChange={(c) => setFilterNeedsFollowup(c)} className="hidden" />
                        <span className={`text-sm ${filterNeedsFollowup ? 'text-yellow-700 dark:text-yellow-400' : 'text-slate-700 dark:text-slate-300'}`}>📋 Followup</span>
                      </label>
                      <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterHotline ? 'bg-purple-200 dark:bg-purple-600/30 border border-purple-400 dark:border-purple-500' : 'bg-white dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-700/50 border border-transparent'}`}>
                        <Checkbox id="filter-hotline" checked={filterHotline} onCheckedChange={(c) => setFilterHotline(c)} className="hidden" />
                        <span className={`text-sm ${filterHotline ? 'text-purple-700 dark:text-purple-400' : 'text-slate-700 dark:text-slate-300'}`}>📞 Hotline</span>
                      </label>
                    </div>
                  </div>
                  
                  {/* Data Flags */}
                  <div className="bg-slate-100 dark:bg-slate-800/30 border border-slate-300 dark:border-slate-700/50 rounded-lg p-3">
                    <Label className="text-xs text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-3 block">Data Filters</Label>
                    <div className="flex flex-wrap gap-2">
                      <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterHasVictims ? 'bg-slate-300 dark:bg-slate-600/50 border border-slate-400 dark:border-slate-500' : 'bg-white dark:bg-slate-800/50 hover:bg-slate-200 dark:hover:bg-slate-700/50 border border-transparent'}`}>
                        <Checkbox id="filter-victims" checked={filterHasVictims} onCheckedChange={(c) => setFilterHasVictims(c)} className="hidden" />
                        <span className={`text-sm ${filterHasVictims ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>Victims</span>
                      </label>
                      <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterHasWitnesses ? 'bg-slate-300 dark:bg-slate-600/50 border border-slate-400 dark:border-slate-500' : 'bg-white dark:bg-slate-800/50 hover:bg-slate-200 dark:hover:bg-slate-700/50 border border-transparent'}`}>
                        <Checkbox id="filter-witnesses" checked={filterHasWitnesses} onCheckedChange={(c) => setFilterHasWitnesses(c)} className="hidden" />
                        <span className={`text-sm ${filterHasWitnesses ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>Witnesses</span>
                      </label>
                      <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterHasPerps ? 'bg-slate-300 dark:bg-slate-600/50 border border-slate-400 dark:border-slate-500' : 'bg-white dark:bg-slate-800/50 hover:bg-slate-200 dark:hover:bg-slate-700/50 border border-transparent'}`}>
                        <Checkbox id="filter-perps" checked={filterHasPerps} onCheckedChange={(c) => setFilterHasPerps(c)} className="hidden" />
                        <span className={`text-sm ${filterHasPerps ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>Perps</span>
                      </label>
                      <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterHasMedia ? 'bg-slate-300 dark:bg-slate-600/50 border border-slate-400 dark:border-slate-500' : 'bg-white dark:bg-slate-800/50 hover:bg-slate-200 dark:hover:bg-slate-700/50 border border-transparent'}`}>
                        <Checkbox id="filter-media" checked={filterHasMedia} onCheckedChange={(c) => setFilterHasMedia(c)} className="hidden" />
                        <span className={`text-sm ${filterHasMedia ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>Media</span>
                      </label>
                    </div>
                  </div>
                </div>
                
                {/* Police & Radio Codes */}
                <div className="bg-slate-100 dark:bg-slate-800/30 border border-slate-300 dark:border-slate-700/50 rounded-lg p-3">
                  <Label className="text-xs text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-3 block">Police & Radio</Label>
                  <div className="flex flex-wrap gap-2">
                    <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterTransmitted1085 ? 'bg-blue-200 dark:bg-blue-600/30 border border-blue-400 dark:border-blue-500' : 'bg-white dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-700/50 border border-transparent'}`}>
                      <Checkbox id="filter-1085" checked={filterTransmitted1085} onCheckedChange={(c) => setFilterTransmitted1085(c)} className="hidden" />
                      <span className={`text-sm font-mono ${filterTransmitted1085 ? 'text-blue-700 dark:text-blue-400' : 'text-slate-700 dark:text-slate-300'}`}>10-85</span>
                    </label>
                    <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterTransmitted1013 ? 'bg-red-200 dark:bg-red-600/30 border border-red-400 dark:border-red-500' : 'bg-white dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-700/50 border border-transparent'}`}>
                      <Checkbox id="filter-1013" checked={filterTransmitted1013} onCheckedChange={(c) => setFilterTransmitted1013(c)} className="hidden" />
                      <span className={`text-sm font-mono ${filterTransmitted1013 ? 'text-red-700 dark:text-red-400' : 'text-slate-700 dark:text-slate-300'}`}>10-13</span>
                    </label>
                    <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterPoliceFollowup ? 'bg-slate-300 dark:bg-slate-600/50 border border-slate-400 dark:border-slate-500' : 'bg-white dark:bg-slate-800/50 hover:bg-slate-200 dark:hover:bg-slate-700/50 border border-transparent'}`}>
                      <Checkbox id="filter-police-followup" checked={filterPoliceFollowup} onCheckedChange={(c) => setFilterPoliceFollowup(c)} className="hidden" />
                      <span className={`text-sm ${filterPoliceFollowup ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>Police Followup</span>
                    </label>
                    <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterPoliceReport ? 'bg-slate-300 dark:bg-slate-600/50 border border-slate-400 dark:border-slate-500' : 'bg-white dark:bg-slate-800/50 hover:bg-slate-200 dark:hover:bg-slate-700/50 border border-transparent'}`}>
                      <Checkbox id="filter-police-report" checked={filterPoliceReport} onCheckedChange={(c) => setFilterPoliceReport(c)} className="hidden" />
                      <span className={`text-sm ${filterPoliceReport ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>Report Filed</span>
                    </label>
                    <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterFootageRequested ? 'bg-slate-300 dark:bg-slate-600/50 border border-slate-400 dark:border-slate-500' : 'bg-white dark:bg-slate-800/50 hover:bg-slate-200 dark:hover:bg-slate-700/50 border border-transparent'}`}>
                      <Checkbox id="filter-footage-req" checked={filterFootageRequested} onCheckedChange={(c) => setFilterFootageRequested(c)} className="hidden" />
                      <span className={`text-sm ${filterFootageRequested ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>Footage Requested</span>
                    </label>
                    <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterFootageObtained ? 'bg-slate-300 dark:bg-slate-600/50 border border-slate-400 dark:border-slate-500' : 'bg-white dark:bg-slate-800/50 hover:bg-slate-200 dark:hover:bg-slate-700/50 border border-transparent'}`}>
                      <Checkbox id="filter-footage" checked={filterFootageObtained} onCheckedChange={(c) => setFilterFootageObtained(c)} className="hidden" />
                      <span className={`text-sm ${filterFootageObtained ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>Footage Obtained</span>
                    </label>
                    <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterFromLiveCall ? 'bg-red-200 dark:bg-red-600/30 border border-red-400 dark:border-red-500' : 'bg-white dark:bg-slate-800/50 hover:bg-slate-200 dark:hover:bg-slate-700/50 border border-transparent'}`}>
                      <Checkbox id="filter-live-call" checked={filterFromLiveCall} onCheckedChange={(c) => setFilterFromLiveCall(c)} className="hidden" />
                      <span className={`text-sm ${filterFromLiveCall ? 'text-red-700 dark:text-red-300' : 'text-slate-700 dark:text-slate-300'}`}>From Live Call</span>
                    </label>
                    <label className={`flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors ${filterHasCallbacks ? 'bg-cyan-200 dark:bg-cyan-600/30 border border-cyan-400 dark:border-cyan-500' : 'bg-white dark:bg-slate-800/50 hover:bg-slate-200 dark:hover:bg-slate-700/50 border border-transparent'}`}>
                      <Checkbox id="filter-callbacks" checked={filterHasCallbacks} onCheckedChange={(c) => setFilterHasCallbacks(!!c)} className="hidden" />
                      <span className={`text-sm ${filterHasCallbacks ? 'text-cyan-700 dark:text-cyan-300' : 'text-slate-700 dark:text-slate-300'}`}>Has Callbacks</span>
                    </label>
                  </div>
                  {/* Callback Type Sub-filter */}
                  {filterHasCallbacks && (
                    <div className="flex items-center gap-2 mt-2 ml-2">
                      <span className="text-xs text-slate-500">Callback type:</span>
                      {[
                        { value: "", label: "All" },
                        { value: "cv", label: "CV" },
                        { value: "3rd_party", label: "3rd Party" },
                        { value: "none", label: "No Type" },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => setFilterCallbackType(filterCallbackType === opt.value ? "" : opt.value)}
                          className={`text-xs px-2 py-0.5 rounded border transition-colors ${
                            filterCallbackType === opt.value
                              ? "bg-cyan-600/30 border-cyan-500 text-cyan-300"
                              : "border-slate-600 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-400 dark:hover:border-slate-500"
                          }`}
                        >{opt.label}</button>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Clear Filters */}
                <div className="flex justify-between items-center pt-2 gap-2">
                  <p className="text-xs text-slate-600 dark:text-slate-500">
                    {filteredIncidents.length} of {incidents.length} incidents match
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearFilters}
                      className="text-xs border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      <X className="w-3 h-3 mr-1" />
                      Clear All
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowFilters(false)}
                      className="text-xs border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 md:hidden"
                    >
                      <ChevronUp className="w-3 h-3 mr-1" />
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Incidents List - Desktop Table View */}
      {viewMode === "cards" && <Card className="bg-slate-900 border-slate-800 hidden md:block">
        <ScrollArea className="h-[calc(100vh-20rem)]" ref={scrollAreaRef}>
          <table className="data-table">
            <thead className="sticky top-0 bg-slate-900">
              <tr>
                {[
                  { key: "id", label: "Incident" },
                  { key: "type", label: "Type" },
                  { key: "location", label: "Location" },
                  { key: null, label: "Units" },
                  { key: "status", label: "Status" },
                  { key: "priority", label: "Priority" },
                  { key: "date", label: "Date/Time" },
                  { key: null, label: "" },
                ].map((col, i) => (
                  <th
                    key={i}
                    className={col.key ? "cursor-pointer select-none hover:text-slate-200" : ""}
                    onClick={() => col.key && handleColumnSort(col.key)}
                  >
                    <span className="flex items-center gap-1">
                      {col.label}
                      {col.key && sortField === col.key && (
                        sortDirection === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedIncidents.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-slate-500">
                    No incidents found
                  </td>
                </tr>
              ) : (
                paginatedIncidents.map((incident) => (
                  <tr 
                    key={incident.incident_id} 
                    onClick={() => navigateToIncident(incident.incident_id)}
                    className="cursor-pointer"
                    data-testid={`incident-row-${incident.incident_id}`}
                  >
                    <td>
                      <div>
                        <p className="font-medium">{incident.headline}</p>
                        <p className="text-xs text-slate-500 font-mono">{incident.incident_id}</p>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-1 flex-wrap">
                        <Badge variant="outline">{incident.incident_type}</Badge>
                        {(incident.secondary_call_type || incident.secondary_incident_type) && (
                          <Badge variant="outline" className="text-xs border-slate-500 text-slate-300">+ {incident.secondary_call_type || incident.secondary_incident_type}</Badge>
                        )}
                        {incident.from_live_call_id && (
                          <Badge className="bg-red-600/20 text-red-400 border-red-600/50 text-[10px]">LIVE</Badge>
                        )}
                        {(() => {
                          // Backend resolves the priority: real arrest record
                          // on this incident → ARREST; else via-pattern (this
                          // incident's RFA was used in another incident's
                          // arrest) → ARREST VIA; else legacy is_arrest
                          // signal → ARREST.
                          if (incident.is_arrest) {
                            return <Badge className="bg-green-600 text-white text-[10px]">ARREST</Badge>;
                          }
                          if (incident.is_arrest_via) {
                            return <Badge className="bg-emerald-800 text-white text-[10px]" title="This incident's Ready-for-Arrest was used in another incident's arrest">ARREST VIA</Badge>;
                          }
                          return null;
                        })()}
                      </div>
                      {renderPhoneNumbers(incident)}
                    </td>
                    <td>
                      <div className="flex items-center gap-1 text-sm">
                        <MapPin className="w-3 h-3 text-slate-500" />
                        <span className="truncate max-w-48">{incident.address}</span>
                      </div>
                    </td>
                    <td className="min-w-[180px]">
                      <div className="flex flex-wrap gap-1">
                        {incident.units_responded?.length > 0 ? (
                          [...incident.units_responded].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).map((unit) => (
                            <Badge key={unit} variant="outline" className="text-xs font-mono">
                              {unit}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-xs text-slate-500">-</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={getStatusClass(incident.status)}>
                        {incident.status?.replace("_", " ")}
                      </span>
                    </td>
                    <td>
                      <Badge className={getPriorityClass(incident.priority)}>
                        {incident.priority}
                      </Badge>
                    </td>
                    <td>
                      <div className="flex flex-col gap-0.5 text-sm text-slate-400">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          <span className="font-mono text-xs">
                            {formatNYTime(incident.date_time)}
                          </span>
                        </div>
                        {showCreatedDate && incident.created_at && (
                          <div className="flex items-center gap-1 text-blue-400/80">
                            <span className="text-[9px] uppercase tracking-wide">Created</span>
                            <span className="font-mono text-[10px]">{formatNYTime(incident.created_at)}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ScrollArea>
      </Card>}

      {/* Incidents Map View — pins for every filtered incident with a
          geocodable address. Pins are rendered from the same sortedIncidents
          list the other views use, so all filters apply. Visible only on
          the device whose toggle picked map (desktop=viewMode,
          mobile=mobileViewMode), so the desktop card/list views aren't
          replaced when only the mobile toggle was set, and vice-versa. */}
      {(viewMode === "map" || mobileViewMode === "map") && (
        <div className={`bg-slate-900 border border-slate-800 rounded-lg overflow-hidden ${
          viewMode === "map" && mobileViewMode === "map" ? "" :
          viewMode === "map" ? "max-md:hidden" :
          "md:hidden"
        }`}>
          {(() => {
            const coordsById = new Map();
            (incidentsMapData || []).forEach(loc => {
              if (loc?.incident_id && typeof loc.lat === "number" && typeof loc.lng === "number") {
                coordsById.set(loc.incident_id, { lat: loc.lat, lng: loc.lng });
              }
            });
            const markers = [];
            sortedIncidents.forEach(inc => {
              const c = coordsById.get(inc.incident_id);
              if (c) markers.push({ ...c, incident: inc });
            });
            // Group only when the address string is 100% identical (case-
            // and whitespace-insensitive) so "1349 President" and "1347
            // President St" stay as separate pins even if Google geocoded
            // them to the same block centroid. Two incidents at exactly
            // the same address get a single pin whose popup lists both.
            const groups = new Map();
            markers.forEach(m => {
              const key = (m.incident.address || "").trim().toLowerCase();
              const g = groups.get(key);
              if (g) {
                g.incidents.push(m.incident);
              } else {
                groups.set(key, { lat: m.lat, lng: m.lng, incidents: [m.incident] });
              }
            });
            const groupedMarkers = Array.from(groups.values());
            const totalGeocoded = coordsById.size;
            const filteredCount = markers.length;
            const totalIncidentsWithAddress = sortedIncidents.filter(i => (i.address || "").trim()).length;
            return (
              <>
                <div className="px-3 py-2 border-b border-slate-800 flex items-center justify-between flex-wrap gap-2 text-xs text-slate-400">
                  <span>
                    Showing <span className="text-slate-200 font-medium">{filteredCount}</span>
                    {" "}of <span className="text-slate-200 font-medium">{sortedIncidents.length}</span> filtered
                    {filteredCount < totalIncidentsWithAddress && (
                      <span className="ml-2 text-amber-400">
                        ({totalIncidentsWithAddress - filteredCount} address{totalIncidentsWithAddress - filteredCount === 1 ? "" : "es"} not yet geocoded)
                      </span>
                    )}
                  </span>
                  <button
                    onClick={() => { setIncidentsMapLoaded(false); fetchIncidentsMapData(); }}
                    className="text-blue-400 hover:text-blue-300 underline"
                    disabled={incidentsMapLoading}
                  >
                    {incidentsMapLoading ? "Refreshing…" : "Refresh"}
                  </button>
                </div>
                <div className="h-[calc(100vh-340px)] min-h-[420px] relative z-0">
                  {incidentsMapLoading && incidentsMapData.length === 0 ? (
                    <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>
                  ) : markers.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-slate-500 text-sm px-4 text-center">
                      No filtered incidents have a geocoded location.
                      {totalGeocoded === 0 && " Try refreshing — the geocoder caches results in batches of 100."}
                    </div>
                  ) : (
                    <MapContainer center={[40.6501, -73.9496]} zoom={13} style={{ height: "100%", width: "100%", background: "#1a1a2e" }} className="rounded-b-lg">
                      <TileLayer
                        attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                        url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                      />
                      <IncidentsMapFitBounds markers={markers} />
                      {groupedMarkers.map(({ lat, lng, incidents }, gIdx) => {
                        const isStacked = incidents.length > 1;
                        const sharedAddress = incidents[0]?.address || "";
                        return (
                          <Marker key={`pin-${gIdx}-${lat.toFixed(5)},${lng.toFixed(5)}`} position={[lat, lng]} icon={incidentMapMarkerIcon}>
                            <Popup maxWidth={320} minWidth={220}>
                              {/* The popup-content-wrapper background is
                                  themed via global CSS in index.css (white
                                  in light, slate-900 in dark, navy in
                                  tactical), so text uses dark: variants to
                                  match — readable on every theme. */}
                              <div className="min-w-[200px] max-w-[300px] text-slate-900 dark:text-slate-100">
                                {isStacked && (
                                  <div className="mb-2 pb-1.5 border-b border-slate-300 dark:border-slate-600">
                                    <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                                      {incidents.length} incidents at this location
                                    </p>
                                    {sharedAddress && (
                                      <p className="text-[11px] text-slate-600 dark:text-slate-300 break-words">{sharedAddress}</p>
                                    )}
                                  </div>
                                )}
                                <div className={isStacked ? "max-h-72 overflow-y-auto space-y-2 pr-0.5" : ""}>
                                  {incidents.map(incident => (
                                    <div
                                      key={incident.incident_id}
                                      className={isStacked ? "pb-2 border-b border-slate-200 dark:border-slate-700/60 last:border-b-0 last:pb-0" : ""}
                                    >
                                      <div className="flex items-center gap-1.5 flex-wrap mb-1">
                                        <Badge className={`${getPriorityClass(incident.priority)} text-[10px]`}>{incident.priority?.toUpperCase()}</Badge>
                                        <span className={`${getStatusClass(incident.status)} text-[10px]`}>{incident.status?.replace("_", " ")}</span>
                                        {incident.is_arrest && <Badge className="bg-emerald-700 text-white text-[10px]">ARREST</Badge>}
                                      </div>
                                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 break-words">{incident.headline || incident.incident_type || "Incident"}</p>
                                      {!isStacked && (
                                        <p className="text-[11px] text-slate-700 dark:text-slate-300 mt-0.5 break-words">{incident.address}</p>
                                      )}
                                      {incident.incident_type && (
                                        <p className="text-[11px] text-slate-600 dark:text-slate-400 mt-0.5">{incident.incident_type}{incident.secondary_call_type || incident.secondary_incident_type ? ` / ${incident.secondary_call_type || incident.secondary_incident_type}` : ""}</p>
                                      )}
                                      <p className="text-[10px] text-slate-500 dark:text-slate-400 font-mono mt-0.5">{incident.incident_id}{incident.date_time ? ` · ${formatNYTime(incident.date_time)}` : ""}</p>
                                      <button
                                        onClick={() => navigateToIncident(incident.incident_id)}
                                        className="mt-1.5 w-full px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded"
                                      >
                                        Open
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </Popup>
                          </Marker>
                        );
                      })}
                    </MapContainer>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Incidents List - Compact List View (Desktop only) */}
      {viewMode === "list" && (
        <div className="hidden md:block bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-800 text-xs text-slate-400 uppercase">
                <tr>
                  {[
                    { key: "id", label: "ID", width: "w-28" },
                    { key: "headline", label: "Headline", width: "" },
                    { key: "type", label: "Type", width: "w-24" },
                    { key: "location", label: "Address", width: "w-36" },
                    { key: "status", label: "Status", width: "w-20" },
                    { key: "priority", label: "Pri", width: "w-16" },
                    { key: "date", label: "Date", width: "w-28" },
                    { key: null, label: "Units", width: "w-20" },
                  ].map((col, i) => (
                    <th
                      key={i}
                      className={`text-left px-3 py-2 ${col.width} ${col.key ? "cursor-pointer select-none hover:text-slate-200" : ""}`}
                      onClick={() => col.key && handleColumnSort(col.key)}
                    >
                      <span className="flex items-center gap-1">
                        {col.label}
                        {col.key && sortField === col.key && (
                          sortDirection === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {paginatedIncidents.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-8 text-slate-500">No incidents found</td></tr>
                ) : paginatedIncidents.map((inc) => (
                  <tr
                    key={inc.incident_id}
                    onClick={() => navigateToIncident(inc.incident_id)}
                    className="cursor-pointer hover:bg-slate-800/50 transition-colors"
                  >
                    <td className="px-3 py-1.5 font-mono text-xs text-slate-400">{inc.incident_id}</td>
                    <td className="px-3 py-1.5 text-slate-200 max-w-xs">
                      <div className="truncate">
                        {inc.headline}
                        {inc.from_live_call_id && <span className="ml-1 text-[10px] text-red-400 font-medium">LIVE</span>}
                        {inc.is_arrest && (
                          <span className="ml-1 text-[10px] text-green-400 font-medium">ARREST</span>
                        )}
                        {!inc.is_arrest && inc.is_arrest_via && (
                          <span className="ml-1 text-[10px] text-emerald-300 font-medium" title="This incident's Ready-for-Arrest was used in another incident's arrest">ARREST VIA</span>
                        )}
                      </div>
                      {renderPhoneNumbers(inc)}
                    </td>
                    <td className="px-3 py-1.5 text-xs text-slate-400 truncate">{inc.incident_type}{(inc.secondary_call_type || inc.secondary_incident_type) ? ` / ${inc.secondary_call_type || inc.secondary_incident_type}` : ""}</td>
                    <td className="px-3 py-1.5 text-xs text-slate-500 truncate">{inc.address}</td>
                    <td className="px-3 py-1.5"><span className={`text-xs ${getStatusClass(inc.status)}`}>{inc.status?.replace("_", " ")}</span></td>
                    <td className="px-3 py-1.5"><span className={`text-xs ${getPriorityClass(inc.priority)}`}>{inc.priority}</span></td>
                    <td className="px-3 py-1.5 text-xs text-slate-400 font-mono">
                      {formatNYTime(inc.date_time)}
                      {showCreatedDate && inc.created_at && (
                        <div className="text-[10px] text-blue-400/80">Created {formatNYTime(inc.created_at)}</div>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-xs text-slate-500">{inc.units_responded?.slice(0, 3).join(", ")}{inc.units_responded?.length > 3 ? "..." : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Incidents List - Mobile Compact View */}
      {mobileViewMode === "compact" && (
        <div className="md:hidden divide-y divide-slate-700 bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
          {paginatedIncidents.length === 0 ? (
            <div className="p-8 text-center text-slate-500">No incidents found</div>
          ) : paginatedIncidents.map((incident) => (
            <div
              key={incident.incident_id}
              onClick={() => navigateToIncident(incident.incident_id)}
              className="flex items-center gap-1.5 px-2 py-1.5 cursor-pointer hover:bg-slate-800/50 active:bg-slate-800"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1 flex-wrap">
                  <Badge variant="outline" className="text-[9px] px-1 py-0 shrink-0">{incident.incident_type}</Badge>
                  {(incident.secondary_call_type || incident.secondary_incident_type) && (
                    <Badge variant="outline" className="text-[9px] px-1 py-0 shrink-0 border-slate-500 text-slate-300">+ {incident.secondary_call_type || incident.secondary_incident_type}</Badge>
                  )}
                  <span className="text-[9px] text-slate-400 truncate">{incident.address || "No address"}</span>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-[9px] text-slate-500 font-mono">{incident.incident_id}</span>
                  <span className="text-[9px] text-slate-500 font-mono">{formatNYTime(incident.date_time)}</span>
                  {showCreatedDate && incident.created_at && (
                    <span className="text-[9px] text-blue-400/80 font-mono">· Created {formatNYTime(incident.created_at)}</span>
                  )}
                  <span className={`text-[9px] px-1 py-0 rounded border leading-tight uppercase ${
                    incident.status === "report" ? "text-purple-400 border-purple-600/50 bg-purple-900/30" :
                    incident.status === "log" ? "text-slate-300 border-slate-600/50 bg-slate-800/50" :
                    incident.status === "arrest" ? "text-green-400 border-green-600/50 bg-green-900/30" :
                    incident.status === "live_call" ? "text-red-400 border-red-600/50 bg-red-900/30" :
                    incident.status === "investigating" ? "text-amber-400 border-amber-600/50 bg-amber-900/30" :
                    incident.status === "closed" ? "text-blue-400 border-blue-600/50 bg-blue-900/30" :
                    "text-slate-400 border-slate-600/50 bg-slate-800/30"
                  }`}>{incident.status?.replace("_", " ")}</span>
                  {incident.from_live_call_id && <span className="text-[9px] px-1 py-0 rounded border leading-tight text-red-400 border-red-600/50 bg-red-900/30">L</span>}
                  {incident.is_arrest && (
                    <span className="text-[8px] text-green-400 font-bold leading-tight">ARREST</span>
                  )}
                  {!incident.is_arrest && incident.is_arrest_via && (
                    <span className="text-[8px] text-emerald-300 font-bold leading-tight" title="This incident's Ready-for-Arrest was used in another incident's arrest">ARREST VIA</span>
                  )}
                </div>
                {renderPhoneNumbers(incident, "9")}
              </div>
              <ChevronRight className="w-3 h-3 text-slate-600 shrink-0" />
            </div>
          ))}
        </div>
      )}

      {/* Incidents List - Mobile Card View */}
      <div className={`md:hidden space-y-3 ${mobileViewMode !== "cards" ? "hidden" : ""}`}>
        {paginatedIncidents.length === 0 ? (
          <Card className="bg-slate-900 border-slate-800 p-8 text-center text-slate-500">
            No incidents found
          </Card>
        ) : (
          paginatedIncidents.map((incident) => (
            <Card
              key={incident.incident_id}
              className={`incident-card-mobile cursor-pointer active:scale-[0.98] transition-transform ${
                incident.priority === 'high' ? 'priority-high' :
                incident.priority === 'critical' ? 'priority-critical' : ''
              }`}
              onClick={() => navigateToIncident(incident.incident_id)}
            >
              <CardContent className="p-0">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1">
                    <p className="font-medium">{incident.headline}</p>
                    <p className="text-xs text-slate-500 font-mono">{incident.incident_id}</p>
                  </div>
                  <Badge className={`${getPriorityClass(incident.priority)} flex-shrink-0`}>
                    {incident.priority}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Badge variant="outline" className="text-xs">{incident.incident_type}</Badge>
                  {(incident.secondary_call_type || incident.secondary_incident_type) && (
                    <Badge variant="outline" className="text-xs border-slate-500 text-slate-300">+ {incident.secondary_call_type || incident.secondary_incident_type}</Badge>
                  )}
                  {incident.from_live_call_id && (
                    <Badge className="bg-red-600/20 text-red-400 border-red-600/50 text-[10px]">LIVE</Badge>
                  )}
                  {incident.is_arrest && (
                    <Badge className="bg-green-600 text-white text-[10px]">ARREST</Badge>
                  )}
                  {!incident.is_arrest && incident.is_arrest_via && (
                    <Badge className="bg-emerald-800 text-white text-[10px]" title="This incident's Ready-for-Arrest was used in another incident's arrest">ARREST VIA</Badge>
                  )}
                  <span className={`${getStatusClass(incident.status)} text-xs`}>
                    {incident.status?.replace("_", " ")}
                  </span>
                </div>
                {renderPhoneNumbers(incident)}
                <div className="flex items-center gap-1 text-sm text-slate-400 mb-2">
                  <MapPin className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">{incident.address || "No address"}</span>
                </div>
                {incident.units_responded?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    <span className="text-xs text-slate-500">Units:</span>
                    {[...incident.units_responded].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).map((unit) => (
                      <Badge key={unit} variant="outline" className="text-xs font-mono px-1.5 py-0">
                        {unit}
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-1 text-xs text-slate-400">
                  <Clock className="w-3 h-3" />
                  <span className="font-mono">{formatNYTime(incident.date_time)}</span>
                  {showCreatedDate && incident.created_at && (
                    <span className="font-mono text-[10px] text-blue-400/80 ml-1">· Created {formatNYTime(incident.created_at)}</span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Bottom Pagination */}
      <div className="pb-20 md:pb-0">
        {renderPagination()}
      </div>

      {/* Floating Action Button - Mobile */}
      <Button
        className="fab md:hidden"
        onClick={() => setShowNewDialog(true)}
      >
        <Plus className="w-6 h-6" />
      </Button>

      {/* Phone Lookup Popup */}
      <PhoneLookupPopup
        phone={phoneLookupNumber}
        open={!!phoneLookupNumber}
        onOpenChange={(open) => { if (!open) setPhoneLookupNumber(null); }}
      />

      {/* Perpetrator Dialog - at top level, same as Extra Details */}
      <Dialog open={showPerpDialog} onOpenChange={(open) => { if (!open) { setShowPerpDialog(false); setEditingPerpIndex(null); resetNewPerp(); } }}>
        <DialogContent className="bg-slate-900 border-slate-700 w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{editingPerpIndex !== null ? 'Edit Perpetrator' : 'Add Perpetrator'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            <div>
              <Label className="text-xs text-amber-400">Current Location</Label>
              <Input value={newPerp.current_location || ""} onChange={(e) => setNewPerp({...newPerp, current_location: e.target.value})} className="input-dark h-8" placeholder="Where perp is now / last seen" />
            </div>
            <div>
              <Label className="text-xs">Direction of Flight</Label>
              <Input value={newPerp.direction_of_flight || ""} onChange={(e) => setNewPerp({...newPerp, direction_of_flight: e.target.value})} className="input-dark h-8" placeholder="Northbound on Main St" />
            </div>
            <div className="grid grid-cols-4 gap-2">
              <div><Label className="text-xs">Sex</Label><select value={newPerp.sex || ""} onChange={(e) => setNewPerp({...newPerp, sex: e.target.value})} className="w-full h-8 rounded-md border border-slate-700 bg-slate-800 px-2 text-sm text-slate-200"><option value="">—</option><option value="Male">Male</option><option value="Female">Female</option></select></div>
              <div><Label className="text-xs">Race</Label><select value={newPerp.race || ""} onChange={(e) => setNewPerp({...newPerp, race: e.target.value})} className="w-full h-8 rounded-md border border-slate-700 bg-slate-800 px-2 text-sm text-slate-200"><option value="">—</option><option value="Black">Black</option><option value="White">White</option><option value="Hispanic">Hispanic</option><option value="Asian">Asian</option></select></div>
              <div><Label className="text-xs">Age</Label><Input value={newPerp.age || ""} onChange={(e) => setNewPerp({...newPerp, age: e.target.value})} className="input-dark h-8" placeholder="~25" /></div>
              <div><Label className="text-xs">Height</Label><Input value={newPerp.height || ""} onChange={(e) => setNewPerp({...newPerp, height: e.target.value})} className="input-dark h-8" placeholder="5'10" /></div>
            </div>
            <div className="grid grid-cols-4 gap-2">
              <div><Label className="text-xs">Weight</Label><Input value={newPerp.weight || ""} onChange={(e) => setNewPerp({...newPerp, weight: e.target.value})} className="input-dark h-8" placeholder="180 lbs" /></div>
              <div><Label className="text-xs">Build</Label><select value={newPerp.build || ""} onChange={(e) => setNewPerp({...newPerp, build: e.target.value})} className="w-full h-8 rounded-md border border-slate-700 bg-slate-800 px-2 text-sm text-slate-200"><option value="">—</option><option value="Slim">Slim</option><option value="Medium">Medium</option><option value="Heavy">Heavy</option></select></div>
              <div><Label className="text-xs">Complexion</Label><Input value={newPerp.complexion || ""} onChange={(e) => setNewPerp({...newPerp, complexion: e.target.value})} className="input-dark h-8" placeholder="Light/Dark" /></div>
              <div><Label className="text-xs">Eye Color</Label><Input value={newPerp.eye_color || ""} onChange={(e) => setNewPerp({...newPerp, eye_color: e.target.value})} className="input-dark h-8" placeholder="Brown" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Hair Color/Style</Label><Input value={newPerp.hair_color_cut || ""} onChange={(e) => setNewPerp({...newPerp, hair_color_cut: e.target.value})} className="input-dark h-8" placeholder="Black, short" /></div>
              <div><Label className="text-xs">Visible Marks/Tattoos</Label><Input value={newPerp.visible_marks || ""} onChange={(e) => setNewPerp({...newPerp, visible_marks: e.target.value})} className="input-dark h-8" placeholder="Scar on left cheek" /></div>
            </div>
            <p className="text-xs text-slate-500 uppercase pt-2">Clothing</p>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs">Hat</Label><Input value={newPerp.hat_color_style || ""} onChange={(e) => setNewPerp({...newPerp, hat_color_style: e.target.value})} className="input-dark h-8" placeholder="Red baseball cap" /></div>
              <div><Label className="text-xs">Shirt</Label><Input value={newPerp.shirt || ""} onChange={(e) => setNewPerp({...newPerp, shirt: e.target.value})} className="input-dark h-8" placeholder="White t-shirt" /></div>
              <div><Label className="text-xs">Jacket/Coat</Label><Input value={newPerp.jacket_coat || ""} onChange={(e) => setNewPerp({...newPerp, jacket_coat: e.target.value})} className="input-dark h-8" placeholder="Black hoodie" /></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs">Pants</Label><Input value={newPerp.pants || ""} onChange={(e) => setNewPerp({...newPerp, pants: e.target.value})} className="input-dark h-8" placeholder="Blue jeans" /></div>
              <div><Label className="text-xs">Shoes</Label><Input value={newPerp.shoes || ""} onChange={(e) => setNewPerp({...newPerp, shoes: e.target.value})} className="input-dark h-8" placeholder="White sneakers" /></div>
              <div><Label className="text-xs">Other Clothing</Label><Input value={newPerp.clothing || ""} onChange={(e) => setNewPerp({...newPerp, clothing: e.target.value})} className="input-dark h-8" placeholder="Backpack" /></div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox id="incident_perp_armed" checked={newPerp.has_weapon || false} onCheckedChange={(checked) => setNewPerp({...newPerp, has_weapon: checked})} />
                <Label htmlFor="incident_perp_armed" className="text-sm text-red-400">Armed</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="incident_perp_arrested" checked={newPerp.was_arrested || false} onCheckedChange={(checked) => setNewPerp({...newPerp, was_arrested: checked})} />
                <Label htmlFor="incident_perp_arrested" className="text-sm text-green-400">Arrested</Label>
              </div>
            </div>
            {newPerp.has_weapon && (
              <div><Label className="text-xs">Weapon Description</Label><Input value={newPerp.weapon_description || ""} onChange={(e) => setNewPerp({...newPerp, weapon_description: e.target.value})} className="input-dark h-8" placeholder="Handgun, knife, etc." /></div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Vehicle</Label><Input value={newPerp.vehicle_description || ""} onChange={(e) => setNewPerp({...newPerp, vehicle_description: e.target.value})} className="input-dark h-8" placeholder="Black sedan" /></div>
              <div><Label className="text-xs">License Plate</Label><Input value={newPerp.license_plate || ""} onChange={(e) => setNewPerp({...newPerp, license_plate: e.target.value})} className="input-dark h-8" placeholder="ABC-1234" /></div>
            </div>
            <div><Label className="text-xs">Notes</Label><Textarea value={newPerp.notes || ""} onChange={(e) => setNewPerp({...newPerp, notes: e.target.value})} className="input-dark" rows={2} placeholder="Additional notes..." /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Name (if known)</Label><Input value={newPerp.name || ""} onChange={(e) => setNewPerp({...newPerp, name: e.target.value})} className="input-dark h-8" placeholder="Name" /></div>
              <div><Label className="text-xs">Address (if known)</Label><Input value={newPerp.address || ""} onChange={(e) => setNewPerp({...newPerp, address: e.target.value})} className="input-dark h-8" placeholder="Address" /></div>
            </div>
            <div className="border-t border-slate-700 pt-3">
              <p className="text-xs text-purple-400 font-medium uppercase mb-2"><UserCircle className="w-3 h-3 inline mr-1" />Link to Profile</p>
              {newPerp.linked_profile_id ? (
                <div className="flex items-center justify-between p-2 bg-purple-900/30 border border-purple-700 rounded">
                  <div className="flex items-center gap-2">
                    <UserCircle className="w-4 h-4 text-purple-400" />
                    <p className="text-sm font-medium text-purple-200">{profiles.find(p => p.profile_id === newPerp.linked_profile_id)?.name || 'Unknown Profile'}</p>
                  </div>
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-red-400" onClick={() => setNewPerp({...newPerp, linked_profile_id: null, linked_profile_name: null})}><X className="w-3 h-3 mr-1" />Unlink</Button>
                </div>
              ) : (
                <>
                  <div className="relative mb-2">
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input value={profileSearchQuery} onChange={(e) => setProfileSearchQuery(e.target.value)} placeholder="Search profiles by name..." className="input-dark pl-8 h-8 text-sm" />
                  </div>
                  {profileSearchQuery && (
                    <ScrollArea className="max-h-32">
                      <div className="space-y-1">
                        {profiles.filter(p => p.name?.toLowerCase().includes(profileSearchQuery.toLowerCase())).slice(0, 5).map(profile => (
                          <div key={profile.profile_id} className="p-2 bg-slate-800 rounded cursor-pointer hover:bg-slate-700 flex items-center justify-between" onClick={() => { setNewPerp({...newPerp, linked_profile_id: profile.profile_id, linked_profile_name: profile.name}); setProfileSearchQuery(""); }}>
                            <div className="flex items-center gap-2 min-w-0">
                              {profile.mugshot_url ? (
                                <img
                                  src={getAuthenticatedMediaUrl(`${API.replace('/api', '')}${profile.mugshot_url}/thumb?size=96`)}
                                  alt=""
                                  width={36}
                                  height={36}
                                  loading="lazy"
                                  decoding="async"
                                  onClick={(e) => { e.stopPropagation(); setProfileMugshotLightbox(`${API.replace('/api', '')}${profile.mugshot_url}`); }}
                                  onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling?.classList.remove('hidden'); }}
                                  className="w-9 h-9 object-cover rounded shrink-0 cursor-zoom-in hover:ring-2 hover:ring-purple-400"
                                />
                              ) : null}
                              <div className={`w-9 h-9 rounded bg-slate-700 flex items-center justify-center shrink-0 ${profile.mugshot_url ? 'hidden' : ''}`}>
                                <UserCircle className="w-4 h-4 text-purple-400" />
                              </div>
                              <p className="text-sm font-medium text-slate-200 truncate">{profile.name || 'Unknown'}</p>
                            </div>
                            {profile.has_arrest_warrant && <Badge className="text-[9px] bg-red-600 px-1">WARRANT</Badge>}
                          </div>
                        ))}
                        {profiles.filter(p => p.name?.toLowerCase().includes(profileSearchQuery.toLowerCase())).length === 0 && (
                          <p className="text-xs text-slate-500 p-2">No matching profiles found</p>
                        )}
                      </div>
                    </ScrollArea>
                  )}
                </>
              )}
            </div>
            <Button className="w-full btn-primary" onClick={() => {
              let updatedPerps;
              if (editingPerpIndex !== null) {
                updatedPerps = [...(newIncident.perp_descriptions || [])];
                updatedPerps[editingPerpIndex] = { ...newPerp, perp_id: updatedPerps[editingPerpIndex]?.perp_id || `perp_${Date.now()}` };
              } else {
                updatedPerps = [...(newIncident.perp_descriptions || []), { ...newPerp, perp_id: `perp_${Date.now()}` }];
              }
              const weaponSync = syncWeaponFromPerps(updatedPerps);
              setNewIncident({ ...newIncident, perp_descriptions: updatedPerps, ...weaponSync });
              setShowPerpDialog(false);
              setEditingPerpIndex(null);
              resetNewPerp();
              setProfileSearchQuery("");
            }}>
              {editingPerpIndex !== null ? 'Update Perpetrator' : 'Add Perpetrator'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Extra Details Dialog */}
      <Dialog open={showExtraDetails} onOpenChange={setShowExtraDetails}>
        <DialogContent className="max-w-lg bg-slate-900 border-slate-700 max-h-[85vh] overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()} onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Additional Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Link to Incident */}
            <div>
              <Label className="text-xs">Link to Incident</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                <Input
                  value={newIncident._linkSearch || newIncident.linked_incident_id || ""}
                  onChange={async (e) => {
                    const val = e.target.value;
                    setNewIncident({ ...newIncident, _linkSearch: val, linked_incident_id: val });
                    if (val.length >= 2) {
                      try {
                        const res = await axios.get(`${API}/incidents`, { params: { search: val, limit: 10 }, withCredentials: true });
                        setNewIncident(prev => ({ ...prev, _linkResults: Array.isArray(res.data) ? res.data : [] }));
                      } catch { setNewIncident(prev => ({ ...prev, _linkResults: [] })); }
                    } else {
                      setNewIncident(prev => ({ ...prev, _linkResults: [] }));
                    }
                  }}
                  className="input-dark h-8 pl-8 text-sm"
                  placeholder="Search by ID, headline, address..."
                />
              </div>
              {(newIncident._linkResults || []).length > 0 && (
                <div className="mt-1 max-h-40 overflow-y-auto bg-slate-800 border border-slate-700 rounded space-y-0.5">
                  {newIncident._linkResults.map(inc => (
                    <div
                      key={inc.incident_id}
                      className="px-2 py-1.5 cursor-pointer hover:bg-slate-700 text-sm"
                      onClick={() => setNewIncident({ ...newIncident, linked_incident_id: inc.incident_id, _linkSearch: "", _linkResults: [] })}
                    >
                      <span className="font-mono text-xs text-blue-400">{inc.incident_id}</span>
                      <span className="text-slate-300 ml-2">{inc.headline}</span>
                      {inc.address && <span className="text-slate-500 ml-1 text-xs">— {inc.address}</span>}
                    </div>
                  ))}
                </div>
              )}
              {newIncident.linked_incident_id && !newIncident._linkSearch && (
                <div className="mt-1 flex items-center gap-2">
                  <Badge variant="outline" className="text-xs text-blue-400">{newIncident.linked_incident_id}</Badge>
                  <button className="text-red-400 text-xs hover:text-red-300" onClick={() => setNewIncident({ ...newIncident, linked_incident_id: "", _linkSearch: "", _linkResults: [] })}>
                    <X className="w-3 h-3 inline" /> Remove
                  </button>
                </div>
              )}
            </div>

            {/* Assigned Detective */}
            <div>
              <Label className="text-xs">Assigned Detective / Officer</Label>
              <Input value={newIncident.assigned_detective || ""} onChange={(e) => setNewIncident({ ...newIncident, assigned_detective: e.target.value })} className="input-dark h-8" placeholder="Detective name..." />
            </div>

            {/* Hate Crime */}
            <div className="flex items-center justify-between border-t border-slate-700/60 pt-3">
              <div className="flex items-center gap-2">
                <Checkbox id="extra_hate_crime" checked={newIncident.is_hate_crime || false} onCheckedChange={(checked) => setNewIncident({ ...newIncident, is_hate_crime: checked })} />
                <Label htmlFor="extra_hate_crime" className="text-sm">Hate Crime</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="extra_hctf" checked={newIncident.hctf_notified || false} onCheckedChange={(checked) => setNewIncident({ ...newIncident, hctf_notified: checked })} />
                <Label htmlFor="extra_hctf" className="text-sm">HCTF Notified</Label>
              </div>
            </div>

            {/* Weapon */}
            <div className="border-t border-slate-700/60 pt-3">
              <div className="flex items-center gap-2 mb-2">
                <Checkbox id="extra_weapon" checked={newIncident.weapon_displayed || false} onCheckedChange={(checked) => setNewIncident({ ...newIncident, weapon_displayed: checked })} />
                <Label htmlFor="extra_weapon" className="text-sm">Weapon Displayed / Used</Label>
              </div>
              {newIncident.weapon_displayed && (
                <Textarea value={newIncident.weapon_notes || ""} onChange={(e) => setNewIncident({ ...newIncident, weapon_notes: e.target.value })} className="input-dark min-h-12" rows={2} placeholder="Weapon type/description..." />
              )}
            </div>

            {/* EMS */}
            <div className="border-t border-slate-700/60 pt-3">
              <div className="flex items-center gap-2 mb-2">
                <Checkbox id="extra_ems" checked={newIncident.ems_required || false} onCheckedChange={(checked) => setNewIncident({ ...newIncident, ems_required: checked })} />
                <Label htmlFor="extra_ems" className="text-sm">EMS Required</Label>
              </div>
              {newIncident.ems_required && (
                <Input value={newIncident.ems_notes || ""} onChange={(e) => setNewIncident({ ...newIncident, ems_notes: e.target.value })} className="input-dark h-8" placeholder="EMS notes..." />
              )}
            </div>

            {/* Injuries */}
            <div className="border-t border-slate-700/60 pt-3">
              <div className="flex items-center gap-2 mb-2">
                <Checkbox id="extra_injuries" checked={newIncident.injuries_reported || false} onCheckedChange={(checked) => setNewIncident({ ...newIncident, injuries_reported: checked })} />
                <Label htmlFor="extra_injuries" className="text-sm">Injuries Reported</Label>
              </div>
              {newIncident.injuries_reported && (
                <Input value={newIncident.injury_notes || ""} onChange={(e) => setNewIncident({ ...newIncident, injury_notes: e.target.value })} className="input-dark h-8" placeholder="Injury details..." />
              )}
            </div>

            {/* Police Follow-up */}
            <div className="border-t border-slate-700/60 pt-3">
              <div className="flex items-center gap-2 mb-2">
                <Checkbox id="extra_police_followup" checked={newIncident.police_followup_required || false} onCheckedChange={(checked) => setNewIncident({ ...newIncident, police_followup_required: checked })} />
                <Label htmlFor="extra_police_followup" className="text-sm">Police Follow-up Required</Label>
              </div>
              {newIncident.police_followup_required && (
                <Input value={newIncident.police_followup_notes || ""} onChange={(e) => setNewIncident({ ...newIncident, police_followup_notes: e.target.value })} className="input-dark h-8" placeholder="Follow-up notes..." />
              )}
            </div>

            {/* Radio & PD */}
            <div className="border-t border-slate-700/60 pt-3 flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Checkbox id="extra_10_85" checked={newIncident.transmitted_10_85 || false} onCheckedChange={(checked) => setNewIncident({ ...newIncident, transmitted_10_85: checked })} />
                <Label htmlFor="extra_10_85" className={`text-sm ${newIncident.transmitted_10_85 ? "text-red-400 font-bold" : ""}`}>10-85</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="extra_10_13" checked={newIncident.transmitted_10_13 || false} onCheckedChange={(checked) => setNewIncident({ ...newIncident, transmitted_10_13: checked })} />
                <Label htmlFor="extra_10_13" className={`text-sm ${newIncident.transmitted_10_13 ? "text-red-400 font-bold" : ""}`}>10-13</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="extra_pd" checked={newIncident.pd_activated || false} onCheckedChange={(checked) => setNewIncident({ ...newIncident, pd_activated: checked })} />
                <Label htmlFor="extra_pd" className="text-sm">PD Activated</Label>
              </div>
            </div>

            {/* Police Report Filed */}
            <div className="border-t border-slate-700/60 pt-3">
              <div className="flex items-center gap-2 mb-2">
                <Checkbox id="extra_report_filed" checked={newIncident.police_report_filed || false} onCheckedChange={(checked) => setNewIncident({ ...newIncident, police_report_filed: checked })} />
                <Label htmlFor="extra_report_filed" className="text-sm">Police Report Filed</Label>
              </div>
              {newIncident.police_report_filed && (
                <Input value={newIncident.police_report_notes || ""} onChange={(e) => setNewIncident({ ...newIncident, police_report_notes: e.target.value })} className="input-dark h-8" placeholder="Report #, notes..." />
              )}
            </div>

            {/* Police Action Notes */}
            <div className="border-t border-slate-700/60 pt-3">
              <Label className="text-xs mb-1 block">Police Response / Action Notes</Label>
              <Textarea value={newIncident.police_action_notes || ""} onChange={(e) => setNewIncident({ ...newIncident, police_action_notes: e.target.value })} className="input-dark min-h-12" rows={2} placeholder="Police response details..." />
            </div>

            {/* Needs Follow-up */}
            <div className="border-t border-slate-700/60 pt-3">
              <div className="flex items-center gap-2 mb-2">
                <Checkbox id="extra_followup" checked={newIncident.needs_followup || false} onCheckedChange={(checked) => setNewIncident({ ...newIncident, needs_followup: checked })} />
                <Label htmlFor="extra_followup" className="text-sm">Needs Follow-up</Label>
              </div>
              {newIncident.needs_followup && (
                <Input value={newIncident.followup_notes || ""} onChange={(e) => setNewIncident({ ...newIncident, followup_notes: e.target.value })} className="input-dark h-8" placeholder="Follow-up notes..." />
              )}
            </div>

            {/* Footage */}
            <div className="border-t border-slate-700/60 pt-3 space-y-2">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox id="extra_footage_req" checked={newIncident.footage_requested || false} onCheckedChange={(checked) => setNewIncident({ ...newIncident, footage_requested: checked })} />
                  <Label htmlFor="extra_footage_req" className="text-sm">Footage Requested</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="extra_footage_obt" checked={newIncident.footage_obtained || false} onCheckedChange={(checked) => setNewIncident({ ...newIncident, footage_obtained: checked })} />
                  <Label htmlFor="extra_footage_obt" className="text-sm">Footage Obtained</Label>
                </div>
              </div>
              {(newIncident.footage_requested || newIncident.footage_obtained) && (
                <Input value={newIncident.footage_locations || ""} onChange={(e) => setNewIncident({ ...newIncident, footage_locations: e.target.value })} className="input-dark h-8" placeholder="Footage locations / notes..." />
              )}
            </div>

            {/* Call Final */}
            <div className="border-t border-slate-700/60 pt-3">
              <Label className="text-xs mb-1 block">Call Final</Label>
              <Input value={newIncident.call_final || ""} onChange={(e) => setNewIncident({ ...newIncident, call_final: e.target.value })} className="input-dark h-8" placeholder="e.g., Report taken, Gone on arrival..." />
            </div>

            <div className="flex justify-end pt-2">
              <Button size="sm" onClick={() => setShowExtraDetails(false)}>Done</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Additional Location Dialog */}
      <Dialog open={showAddLocDialog} onOpenChange={setShowAddLocDialog}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700" onPointerDownOutside={(e) => e.preventDefault()} onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{editingLocIndex === "original" ? "Location Details" : (editingLocIndex !== null ? "Edit Additional Location" : "Add Additional Location")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Title <span className="text-xs text-slate-500 font-normal">(e.g. "Second Location", "Perp Last Seen")</span></Label>
              <Input
                value={addLocData.title || ""}
                onChange={(e) => setAddLocData({ ...addLocData, title: e.target.value })}
                placeholder="Location title..."
                className="input-dark"
              />
            </div>
            {/* For the original location the address is edited inline on the
                form, so the dialog only collects the extra details. */}
            {editingLocIndex !== "original" && (
            <div className="relative">
              <Label>Location</Label>
              <Input
                ref={addLocInputRef}
                value={addLocData.location}
                onChange={(e) => handleAddLocLocationChange(e.target.value)}
                onFocus={() => { if (addLocPredictions.length > 0) setShowAddLocPredictions(true); }}
                onBlur={() => {
                  setTimeout(() => setShowAddLocPredictions(false), 200);
                  const a = (addLocData.location || "").trim();
                  if (enableGoogleLookup && hasHouseNumber(a) && /^\d+[-\w]*\s+.*[a-z]{3,}/i.test(a)) {
                    fetchCrossStreets(a, (cs) => setAddLocData(prev => ({ ...prev, cross_streets: cs })));
                  }
                }}
                placeholder="Enter location..."
                className="input-dark"
                autoComplete="off"
              />
              {showAddLocPredictions && addLocPredictions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {addLocPredictions.map((prediction, idx) => (
                    <div
                      key={prediction.place_id || idx}
                      className="px-3 py-2 cursor-pointer text-sm hover:bg-slate-700 border-b border-slate-700/50 last:border-0"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => selectAddLocPrediction(prediction)}
                    >
                      <div className="font-medium text-slate-200">{prediction.main_text}</div>
                      {prediction.secondary_text && <div className="text-xs text-slate-400">{prediction.secondary_text}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}
            <div>
              <Label>Location Notes</Label>
              <Textarea
                value={addLocData.location_notes}
                onChange={(e) => setAddLocData({ ...addLocData, location_notes: e.target.value })}
                placeholder="Landmarks, apt #, etc..."
                className="input-dark min-h-12"
                rows={2}
              />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <Label>Cross Streets</Label>
                  <Input
                    value={addLocData.cross_streets}
                    onChange={(e) => setAddLocData({ ...addLocData, cross_streets: e.target.value })}
                    placeholder="Cross streets..."
                    className="input-dark"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-5 px-3"
                  disabled={addLocCrossLoading || !addLocData.location}
                  onClick={() => {
                    if (addLocData.location.length >= 5) {
                      setAddLocCrossLoading(true);
                      fetchCrossStreets(addLocData.location, (cs) => {
                        setAddLocData(prev => ({ ...prev, cross_streets: cs }));
                        setAddLocCrossLoading(false);
                      });
                    }
                  }}
                >
                  {addLocCrossLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div>
              <Label>Direction of Flight</Label>
              <Input
                value={addLocData.direction_of_flight}
                onChange={(e) => setAddLocData({ ...addLocData, direction_of_flight: e.target.value })}
                placeholder="e.g., Northbound on Main St..."
                className="input-dark"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setShowAddLocDialog(false)}>Cancel</Button>
              <Button size="sm" onClick={saveAddLoc}>
                {editingLocIndex !== null ? "Save Changes" : "Add Location"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Callback Number Confirmation */}
      <AlertDialog open={deleteCallbackIndex !== null} onOpenChange={(open) => { if (!open) setDeleteCallbackIndex(null); }}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">Remove Callback Number?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteCallbackIndex !== null && newIncident.callback_numbers?.[deleteCallbackIndex] && (
                <span>
                  Remove <span className="text-white font-medium">
                    {newIncident.callback_numbers[deleteCallbackIndex].number}
                  </span>? This cannot be undone.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => {
              setNewIncident(prev => ({ ...prev, callback_numbers: prev.callback_numbers.filter((_, i) => i !== deleteCallbackIndex) }));
              setDeleteCallbackIndex(null);
            }}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Additional Location Confirmation */}
      <AlertDialog open={deleteLocIndex !== null} onOpenChange={(open) => { if (!open) setDeleteLocIndex(null); }}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">Remove Location?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteLocIndex !== null && newIncident.additional_locations?.[deleteLocIndex] && (
                <span>
                  Remove <span className="text-white font-medium">
                    {newIncident.additional_locations[deleteLocIndex].title || newIncident.additional_locations[deleteLocIndex].location}
                  </span>? This cannot be undone.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={confirmDeleteAddLoc}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {profileMugshotLightbox && (
        <ImageLightbox
          src={profileMugshotLightbox}
          alt="Profile mugshot"
          mediaType="image"
          onClose={() => setProfileMugshotLightbox(null)}
        />
      )}
    </div>
  );
}
